/* eslint-disable camelcase */
/* eslint complexity: "off" */

import { SIZE_INVENTORY } from '../defaults';
import tmsTools from '@utils/tmsTools';
import { gptPrintLog, dfpCheckSite, sizeNormalizer, getDevice } from './helpers';
import deviceAdvFreq from '@utils/deviceAdvFreq';
import { gptLogger } from './loggers';
import { runGptExtensions, getQueryParam } from '@utils/generalUtils';
import CryptoJS from 'crypto-js';

export default function (gptConfig, gptExtensions, controllers) {
    window.rcsOrchestrator?.ptm?.pushTracking({
        label: 'gpt_tag_start',
        funnel: 'adv',
    });

    // Utility variables
    const siteNameadv = RCSAD_sitepage.split('/')[0] || RCSAD_sitepage;
    const isMobile = deviceAdvFreq.mobile();
    const isTablet = deviceAdvFreq.tablet();
    const isDesktop = !(isMobile || isTablet);

    if (window.location.hostname.includes('youreporter.it')) return;

    // Definisco RCSAD_listpos di default se non presente
    if (typeof window.RCSAD_listpos === 'undefined') {
        if (isMobile) {
            // Mobile
            window.RCSAD_listpos = 'Position1,TopLeft,Frame1';
        } else {
            // Desktop
            window.RCSAD_listpos = 'Position1,TopLeft,Frame1,Frame2';
        }
    }

    // Create a separate object for batches based on the presence of the position in RCSAD_listpos
    // Keep only SRA positions in the original object; if the position is not in the listpos, it's either batch candidate or unused
    const listposArray = RCSAD_listpos.split(',');

    gptConfig.context.batchAdSizes = {};

    for (const [key, value] of Object.entries(gptConfig.context.adSizes)) {
        if (!listposArray.includes(key) || value.length === 0) {
            gptConfig.context.batchAdSizes[key] = gptConfig.context.adSizes[key];
            delete gptConfig.context.adSizes[key];
        }
    }

    // Prebid Server Test
    if (typeof gptConfig.prebid.server === 'undefined') {
        gptConfig.prebid.server = true;
    }

    // GPT Version Log
    gptLogger.log('GPT TAG DM 63.91 - fix skinLegacy');
    gptLogger.debug('gptConfig loaded', gptConfig);

    /* Start Adv Process */
    window.googletag = window.googletag || {};
    window.googletag.cmd = window.googletag.cmd || [];

    // Load GPT.js if not in head
    const injectGptJs = function () {
        if (document.querySelector('[src*="/tag/js/gpt.js"]')) return;
        gptLogger.debug(`Google's gpt.js not found, injecting it now`);

        const gads = document.createElement('script');

        gads.async = true;
        gads.type = 'text/javascript';
        gads.src = (document.location.protocol === 'https:' ? 'https' : 'http') + '://  s';
        document.head.insertBefore(gads, document.head.firstChild);
    };

    if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', () => {
            injectGptJs();
        });
    } else {
        injectGptJs();
    }

    // Permutive
    if (gptConfig.permutive === true) {
        if (typeof window._permutivefired === 'undefined') {
            document.addEventListener('_permutivefired', () => {
                controllers.initGptTagController.update((current) => ({
                    ...current,
                    permutive: true,
                }));
            });
        } else {
            controllers.initGptTagController.update((current) => ({
                ...current,
                permutive: true,
            }));
        }
    }

    // Reload initGptTag in single page applications
    document.addEventListener('singlePageApplicationReload', () => {
        gptLogger.debug('SPA reload triggered by singlePageApplicationReload event');
        restartAdv();
    });

    // Reload initGptTag in BFCache reloaded page
    let isBFCacheReloaded = false;

    window.addEventListener('pageshow', (event) => {
        // Console.log('La pagina è visibile');
        if (event.persisted) {
            isBFCacheReloaded = true;
            window.RcsDataLayer.send_page_bottom();
            restartAdv();
        }
    });

    // Reload initGptTag
    function restartAdv() {
        if (window.googletag && window.googletag.pubads) {
            const slots = window.googletag.pubads().getSlots();

            if (slots && slots.length > 0) {
                window.googletag.destroySlots();
            }
        }

        if (!window.prebidVideoLoaded) {
            window.prebidVideoLoaded = true;
        }

        if (window.location.href.indexOf('fantacampionato.gazzetta.it') > -1) {
            window.googletag = null;
        }

        window.RcsDataLayer.advShouldRun = 'true';
        controllers.initGptTagController.reset();
        // InitGptTag();
    }

    // Prebid & Amazon Parallel Auction
    function executeParallelAuction(request, gptSlots, apsSlots) {
        const reason = {
            timeout: 'Timeout',
            grace: 'Grace Period',
            directRefresh: 'Direct Refresh',
            complete: 'Auction Complete',
            error: 'Error',
        };

        const auctionState = {
            startTime: performance.now(),
            apsDone: false,
            prebidDone: false,
            gracePeriod: 0, // Check internal prebid timeout too
        };

        let adServerRequestTriggered = false;
        let graceTimerActive = false;

        // Validate required slot configurations
        if (!gptSlots || gptSlots.length === 0) {
            gptPrintLog(request, 'No GPT slots. Stop auction, please check.');

            return;
        }

        if (!apsSlots || !apsSlots.length) {
            gptPrintLog(request, 'No APS slots. Skipping auction, doing direct refresh.');
            sendAdserverRequest(reason.directRefresh);

            return;
        }

        // Filter out disabled APS slots
        apsSlots = apsSlots.filter((slot) => {
            const excluded = gptConfig?.prebid?.amazonExcluded ?? [
                'rcsad_Bottom',
                'rcsad_Bottom1',
                'rcsad_Bottom2',
                'rcsad_Bottom3',
                'rcsad_Left1',
                'rcsad_Left2',
            ];

            return !excluded.includes(slot.slotID);
        });

        function logElapsed(label) {
            const elapsed = Math.round(performance.now() - auctionState.startTime);

            gptPrintLog(request, label + ' after ' + elapsed + 'ms');
        }

        function sendAdserverRequest(triggerReason) {
            if (adServerRequestTriggered) {
                return;
            }

            adServerRequestTriggered = true;

            logElapsed('Sending ad server request');
            gptLogger.log('...auction ended');
            window.rcsOrchestrator?.ptm?.pushTracking({
                label: 'auction_done',
                funnel: 'adv',
            });

            googletag.cmd.push(() => {
                runGptExtensions(gptExtensions, 'beforeRefresh');
                gptLogger.log(`${request} refresh on ${triggerReason}`);
                gptPrintLog(request, 'Refresh on ' + triggerReason);
                googletag.pubads().refresh(gptSlots);

                runGptExtensions(gptExtensions, 'afterRefresh');

                window.rcsOrchestrator?.ptm?.pushTracking({
                    label: 'ad_request',
                    funnel: 'adv',
                    info: {
                        request,
                        trigger: triggerReason,
                    },
                });
            });
        }

        function executeApsRequest() {
            return new Promise((resolve) => {
                apstag.fetchBids({ slots: apsSlots }, () => {
                    googletag.cmd.push(() => {
                        apstag.setDisplayBids();
                        auctionState.apsDone = true;

                        logElapsed('APS response ✓');

                        window.rcsOrchestrator?.ptm?.pushTracking({
                            label: 'aps_response',
                            funnel: 'adv',
                            time: Math.round(performance.now() - auctionState.startTime),
                        });

                        resolve('APS complete');
                    });
                });
            });
        }

        function executePrebidRequest() {
            return new Promise((resolve) => {
                pbjs.que.push(() => {
                    const timer = performance.now();

                    window.rcsOrchestrator?.ptm?.pushTracking({
                        label: 'prebid_request',
                        funnel: 'adv',
                    });
                    pbjs.rp.requestBids({
                        gptSlotObjects: gptSlots,
                        callback() {
                            pbjs.setTargetingForGPTAsync();
                            googletag.cmd.push(() => {
                                auctionState.prebidDone = true;

                                logElapsed('Prebid response ✓');
                                const prebidBids = pbjs.getBidResponses();

                                gptPrintLog(request, prebidBids);

                                window.rcsOrchestrator?.ptm?.pushTracking({
                                    label: 'prebid_response',
                                    funnel: 'adv',
                                    time: Math.round(performance.now() - timer),
                                });

                                resolve('Prebid complete');
                            });
                        },
                    });
                });
            });
        }

        function timeoutPromise(duration) {
            return new Promise((resolve) => {
                setTimeout(() => {
                    window.rcsOrchestrator?.ptm?.pushTracking({
                        label: 'bidTimeout',
                        funnel: 'adv',
                        time: duration,
                    });
                    resolve(reason.timeout);
                }, duration);
            });
        }

        gptLogger.log('Parallel auction started...');
        window.rcsOrchestrator?.ptm?.pushTracking({
            label: 'auction',
            funnel: 'adv',
        });

        Promise.race([Promise.all([executeApsRequest(), executePrebidRequest()]), timeoutPromise(gptConfig.FAILSAFE_TIMEOUT)])
            .then((result) => {
                if (result === reason.timeout) {
                    logElapsed('Failsafe timeout ⏱ (' + gptConfig.FAILSAFE_TIMEOUT + 'ms)');
                    if (
                        auctionState.gracePeriod > 0 &&
                        auctionState.apsDone !== auctionState.prebidDone // Only one bidder responded
                    ) {
                        graceTimerActive = true;
                        gptPrintLog(request, 'Grace period triggered (' + auctionState.gracePeriod + 'ms)');
                        setTimeout(() => {
                            graceTimerActive = false;

                            if (auctionState.apsDone && auctionState.prebidDone) {
                                sendAdserverRequest(reason.grace);
                            } else {
                                sendAdserverRequest(reason.timeout);
                            }
                        }, auctionState.gracePeriod);
                    } else {
                        sendAdserverRequest(reason.timeout);
                    }
                } else if (!graceTimerActive) {
                    sendAdserverRequest(reason.complete);
                }
            })
            .catch((error) => {
                const message = error && error.message ? error.message : String(error);

                gptPrintLog(request, 'Auction error: ' + message);
                sendAdserverRequest(reason.error);
            });
    }

    if (gptConfig.stickyON) {
        gptPrintLog('ATTIVO stickyON');
        const stickyScriptId = 'stickyScript';

        if (!document.getElementById(stickyScriptId)) {
            gptPrintLog('[STICKY] include stickyScript');
            const stickyScript = document.createElement('script'); // StickyScript

            stickyScript.id = stickyScriptId;
            stickyScript.type = 'text/javascript';
            if (dfpCheckSite('unionesarda')) {
                stickyScript.src = 'https://components2.rcsobjects.it/rcs_adv/v1/distro/dfp/customScript/restyling-sticky-partner-unionesarda.js'; // UNIONESARDA
            } else if (dfpCheckSite('gazzettadelsud')) {
                stickyScript.src = 'https://components2.rcsobjects.it/rcs_adv/v1/distro/dfp/customScript/stickyTools_gazzettadelsud.js'; // GAZZETTADELSUD
            } else if (dfpCheckSite('gds')) {
                stickyScript.src = 'https://components2.rcsobjects.it/rcs_adv/v1/distro/dfp/customScript/stickyTools_gds.js'; // GIORNALE DI SICILIA
            } else if (dfpCheckSite('dmove')) {
                stickyScript.src = 'https://components2.rcsobjects.it/rcs_adv/v1/distro/dfp/customScript/stickyTools_dmove.js'; // DMOVE
            } else if (dfpCheckSite('leitv')) {
                stickyScript.src = 'https://components2.rcsobjects.it/rcs_adv/v1/distro/dfp/customScript/stickyTools_leitv.js'; // LEITV
            } else if (dfpCheckSite('quinews')) {
                stickyScript.src = 'https://components2.rcsobjects.it/rcs_adv/v1/distro/dfp/customScript/stickyTools_quinews.js'; // QUINEWS
            } else if (dfpCheckSite('calciomercato')) {
                stickyScript.src = 'https://components2.rcsobjects.it/rcs_adv/v1/distro/dfp/customScript/stickyTools_calciomercato.js'; // CALCIOMERCATO
            } else if (dfpCheckSite('ilbianconero')) {
                stickyScript.src = 'https://components2.rcsobjects.it/rcs_adv/v1/distro/dfp/customScript/stickyTools_ilbianconero.js'; // ILBIANCONERO
            } else if (dfpCheckSite('lasicilia')) {
                stickyScript.src = 'https://components2.rcsobjects.it/rcs_adv/v1/distro/dfp/customScript/stickyTools_lasicilia.js'; // LASICILIA
            } else {
                stickyScript.src = 'https://components2.rcsobjects.it/rcs_adv/v1/distro/dfp/customScript/restyling-sticky-partner.js'; // DEFAULT
            }

            document.head.appendChild(stickyScript);
        }
    }

    if (gptConfig.hideTlonTabPortrait) {
        const hideTlPortraitTab = document.createElement('link');

        hideTlPortraitTab.href = 'https://components2.rcsobjects.it/rcs_adv/v1/distro/dfp/common/css/hideTlPortraitTab.css';
        hideTlPortraitTab.rel = 'stylesheet';
        hideTlPortraitTab.type = 'text/css';
        document.head.appendChild(hideTlPortraitTab);
    }

    if (window.RCSAD_delayAdRequest) {
        document.addEventListener('RcsVideoExt:AD_OR_VIDEO_STARTED', () => {
            window.RCSAD_delayAdRequest = false;
            controllers.initGptTagController.update((current) => ({
                ...current,
                delayAdRequest: true,
            }));
        });
    }

    const initGptTagSubscriber = controllers.initGptTagController.subscribe((value) => {
        const valuesArray = Object.values(value);
        const canFireInitGptTag = valuesArray.every(Boolean);

        if (canFireInitGptTag) {
            gptLogger.log('initGptTag controller fired', value);
            initGptTag();
            initGptTagSubscriber.unsubscribe();
        } else {
            const falsesOnly = Object.keys(value)
                .filter((key) => value[key] === false)
                .join(', ');

            gptLogger.log('initGptTag controller is waiting for', falsesOnly);
        }
    });

    // Init GPT-TAG
    function initGptTag() {
        window.rcsOrchestrator?.ptm?.pushTracking({
            label: 'init_gpt_tag',
            funnel: 'adv',
        });
        runGptExtensions(gptExtensions, 'beforeInitGptTag');

        if (RCSAD_sitepage === 'gazzetta.it/magic' && window.location.href.includes('fantaduello/home')) {
            gptPrintLog('Adv Process', 'disabled on magic.gazzetta home fantaduello');

            return;
        }

        /* Default isPartner */
        if (typeof gptConfig.isPartner === 'undefined') {
            gptConfig.isPartner = false;
        }

        /* Load Libraries Start */

        if (!gptConfig.testPreloadLibraries) {
            // Load Prebid Server
            if (gptConfig.prebid.activation === true) {
                if (gptConfig.prebid.server === true) {
                    gptPrintLog('Prebid Server', 'enabled');

                    (function (ps) {
                        gptPrintLog('Prebid Server', 'loading js');

                        const script = document.createElement('script');

                        script.async = true;
                        script.src = ps;
                        script.onload = function () {
                            gptPrintLog('Prebid Server', 'loaded js');
                            gptPrintLog('Prebid Server', pbjs.version);

                            const eventPrebidServerLoaded = new Event('eventPrebidServerLoaded');

                            window.dispatchEvent(eventPrebidServerLoaded);
                        };

                        const s = document.getElementsByTagName('script')[0];

                        s.parentNode.insertBefore(script, s);
                    })('//micro.rubiconproject.com/prebid/dynamic/10814.js');
                }
            } else {
                gptPrintLog('Prebid', 'disabled');
            }

            // Load APS
            if (gptConfig.prebid.activation === true && gptConfig.prebid.amazon === true && !window.apstag) {
                gptPrintLog('APS', 'loading js');

                // eslint-disable-next-line max-params
                (function (a9, a, p, s, t, A, g) {
                    if (a[a9]) return;
                    function q(c, r) {
                        a[a9]._Q.push([c, r]);
                    }

                    a[a9] = {
                        init(...args) {
                            q('i', args);
                        },
                        fetchBids(...args) {
                            q('f', args);
                        },
                        setDisplayBids() {},
                        targetingKeys() {
                            return [];
                        },
                        _Q: [],
                    };
                    A = p.createElement(s);
                    A.async = !0;
                    A.src = t;
                    g = p.getElementsByTagName(s)[0];
                    g.parentNode.insertBefore(A, g);
                })('apstag', window, document, 'script', '//c.amazon-adsystem.com/aax2/apstag.js');

                // Initialize APS
                apstag.init({
                    pubID: '3692',
                    adServer: 'googletag',
                    videoAdServer: 'DFP',
                });
            } else {
                gptPrintLog('APS', 'disabled');
            }
        }

        /* Load Libraries End */

        // REMOVE FROM GPT - GPT-TAG-DM PARTNERS - ADVDFPREFRESH?
        let cookieValue;

        if (
            gptConfig.isPartner &&
            ((tmsTools.getCookie('cb-enabled') === null && RCSAD_sitepage.includes('unionesarda.it')) ||
                (tmsTools.getCookie('cookie-agreed') === null &&
                    tmsTools.getCookie('euconsent') === null &&
                    (RCSAD_sitepage.includes('La7.it') || RCSAD_sitepage.includes('tgla7.it'))) ||
                (tmsTools.getCookie('cookieconsent_dismissed') === null && RCSAD_sitepage.includes('sedanoallegro.it')) ||
                (tmsTools.getCookie('cookie_eu_consented') === null && RCSAD_sitepage.includes('corriere.it/tecnologia/dday')) ||
                (tmsTools.getCookie('__ric') === null && tmsTools.getCookie('cpmt_xa') === null && RCSAD_sitepage.includes('leitv.it')))
        ) {
            cookieValue = document.location.href + '?refresh_ce_cp';
        }

        let sizeCurPosDfpRcsTl;
        let sizeCurPosDfpRcsBl;

        window.googletag = window.googletag || {};
        window.googletag.cmd = window.googletag.cmd || [];
        googletag.cmd.push(() => {
            window.gptadslots = []; // Inizializzo array generico
            window.gptadslotsSRA = []; // Inizializzo array posizioni in SRA
            window.gptadslotsSRAamz = []; // Inizializzo array posizioni in SRA Amazon

            // Valorizzo tipopaginavalue solo se non è già specificato come eccezione
            if (typeof gptConfig.context.pageType === 'undefined') {
                const pageType = gptConfig.context.pageTypeMap[window.tipologia.toLowerCase()];

                if (pageType) {
                    gptConfig.context.pageType = pageType;
                } else {
                    gptConfig.context.pageType = 'altro';
                }

                gptLogger.debug(`pageType found in map: ${gptConfig.context.pageType}`);
            } else {
                gptLogger.debug(`pageType pre-set: ${gptConfig.context.pageType}`);
            }

            // GPT-TAG-DM PARTNERS - TIPOPAGINA

            if (gptConfig.isPartner) {
                const isLA7Dev = window.location.href.indexOf('iruobnfdjs22.la7') > -1;

                // REMOVE FROM GPT
                if (typeof window.im_rcs_tipologia !== 'undefined') {
                    // GDS STAGING
                    switch (window.im_rcs_tipologia.toLowerCase()) {
                        case 'gallery':
                            window.tipopaginavalue = 'photogallery';
                            break;
                        default:
                            window.tipopaginavalue = window.im_rcs_tipologia.toLowerCase();
                    }
                } else if (typeof window.RCSAD_tipo_pagina !== 'undefined') {
                    // OPEN DEV
                    switch (window.RCSAD_tipo_pagina.toLowerCase()) {
                        case 'homepage':
                            window.tipopaginavalue = 'homepage';
                            break;
                        case 'articolo':
                            window.tipopaginavalue = 'articolo';
                            break;
                        default:
                            window.tipopaginavalue = window.RCSAD_tipo_pagina;
                    }
                } else if (typeof window.tipologia !== 'undefined') {
                    // LA SICILIA
                    switch (window.tipologia.toLowerCase()) {
                        case 'gallery':
                            window.tipopaginavalue = 'photogallery';
                            break;
                        default:
                            window.tipopaginavalue = window.tipologia.toLowerCase();
                    }
                } else if (typeof window.tipo_pagina !== 'undefined') {
                    if (typeof window.LA7_pagetype === 'undefined') {
                        // TUTTO (NON LA7)
                        switch (window.tipo_pagina.toLowerCase()) {
                            case 'home':
                                window.tipopaginavalue = 'homepage';
                                break;
                            case 'fotogallery':
                                window.tipopaginavalue = 'photogallery';
                                break;
                            case 'bigcard':
                                window.tipopaginavalue = 'photogallery';
                                break;
                            case 'gallery':
                                window.tipopaginavalue = 'photogallery';
                                break;
                            default:
                                window.tipopaginavalue = window.tipo_pagina;
                                break;
                        }

                        if (window.tipo_pagina.toLowerCase() === 'pag_con_video') {
                            window.tipopaginavalue = 'video';
                        }

                        if (window.tipo_pagina.toLowerCase() === 'pag_senza_video') {
                            window.tipopaginavalue = 'altro';
                        }

                        if (window.tipo_pagina.toLowerCase() === 'sezione') {
                            window.tipopaginavalue = 'homepage';
                        }

                        let isPagSezPodcast;

                        if (isLA7Dev) {
                            isPagSezPodcast =
                                typeof tipo_pagina !== 'undefined' &&
                                (tipo_pagina === 'homepage' || tipo_pagina === 'pag_senza_video') &&
                                typeof sc_sezione2 !== 'undefined' &&
                                sc_sezione2 === 'podcast' &&
                                (typeof sc_sezione3 === 'undefined' || sc_sezione3 === '');
                        } else {
                            isPagSezPodcast =
                                typeof tipo_pagina !== 'undefined' &&
                                (tipo_pagina === 'homepage' || tipo_pagina === 'pag_senza_video') &&
                                typeof sc_sezione1 !== 'undefined' &&
                                sc_sezione1 === 'podcast' &&
                                (typeof sc_sezione2 === 'undefined' || sc_sezione2 === '');
                        }

                        if (isPagSezPodcast) {
                            window.tipopaginavalue = 'homepage_sezione';
                        }
                    } else if (RCSAD_sitepage.indexOf('leitv.it/') > -1) {
                        // LEITV TIPOPAGINA = LAYOUT
                        if (typeof window.La7_layout === 'undefined') {
                            // LEITV TIPOPAGINA = TIPOPAGINA
                            window.tipopaginavalue = window.tipo_pagina;
                        } else {
                            switch (window.LA7_pagetype.toLowerCase()) {
                                case 'homepage':
                                case 'listing':
                                    window.tipopaginavalue = 'homepage';
                                    break;
                                case 'video':
                                    window.tipopaginavalue = 'articolo';
                                    break;
                                default:
                                    window.tipopaginavalue = window.tipo_pagina;
                            }
                        }
                    } else {
                        // LA7 - TG LA7

                        switch (window.LA7_pagetype.toLowerCase()) {
                            case 'photogallery':
                                window.tipopaginavalue = 'photogallery';
                                break;
                            case 'sezione':
                                window.tipopaginavalue = 'homepage_sezione';
                                break;
                            case 'listing':
                                window.tipopaginavalue = 'homepage_sezione';
                                break;
                            default:
                                window.tipopaginavalue = window.LA7_pagetype;
                                break;
                        }

                        const isPagFogliaVideoRivediLA7 =
                            typeof tipo_pagina !== 'undefined' &&
                            (tipo_pagina === 'video' || tipo_pagina === 'pag_con_video') &&
                            typeof sc_sezione1 !== 'undefined' &&
                            sc_sezione1 === 'rivedila7' &&
                            typeof sc_sezione2 !== 'undefined' &&
                            sc_sezione2.length > 0;
                        let isPagSezPodcast;

                        if (isLA7Dev) {
                            isPagSezPodcast =
                                typeof tipo_pagina !== 'undefined' &&
                                (tipo_pagina === 'homepage' || tipo_pagina === 'pag_senza_video') &&
                                typeof sc_sezione2 !== 'undefined' &&
                                sc_sezione2 === 'podcast' &&
                                (typeof sc_sezione3 === 'undefined' || sc_sezione3 === '');
                        } else {
                            isPagSezPodcast =
                                typeof tipo_pagina !== 'undefined' &&
                                (tipo_pagina === 'homepage' || tipo_pagina === 'pag_senza_video') &&
                                typeof sc_sezione1 !== 'undefined' &&
                                sc_sezione1 === 'podcast' &&
                                (typeof sc_sezione2 === 'undefined' || sc_sezione2 === '');
                        }

                        const isPagFogliaPodcast =
                            typeof tipo_pagina !== 'undefined' &&
                            tipo_pagina === 'pag_senza_video' &&
                            typeof sc_sezione1 !== 'undefined' &&
                            sc_sezione1 === 'podcast' &&
                            typeof sc_sezione2 !== 'undefined' &&
                            sc_sezione2 !== '';

                        if (isPagFogliaVideoRivediLA7) {
                            window.tipopaginavalue = 'video';
                        } else if (isPagSezPodcast) {
                            window.tipopaginavalue = 'homepage_sezione';
                        } else if (isPagFogliaPodcast) {
                            window.tipopaginavalue = 'altro';
                        }

                        const isHpSezLA7Teche =
                            typeof sc_sezione !== 'undefined' &&
                            sc_sezione === 'la7teche' &&
                            (typeof sc_sezione1 === 'undefined' || sc_sezione1 === '');
                        const isHpSezProgrammi =
                            typeof sc_sezione !== 'undefined' &&
                            sc_sezione === 'programmi' &&
                            (typeof sc_sezione1 === 'undefined' || sc_sezione1 === '');

                        if (isHpSezLA7Teche || isHpSezProgrammi) {
                            window.tipopaginavalue = 'homepage_sezione';
                        }
                    }
                } else if (typeof window.RCSAD_type_page === 'string') {
                    // DDAY
                    switch (window.RCSAD_type_page.toLowerCase()) {
                        case 'fotogallery':
                            window.tipopaginavalue = 'photogallery';
                            break;
                        case 'gallery_mobile':
                            window.tipopaginavalue = 'photogallery';
                            break;
                        case 'bigcard':
                            window.tipopaginavalue = 'photogallery';
                            break;
                        case 'gallery':
                            window.tipopaginavalue = 'photogallery';
                            break;
                        default:
                            window.tipopaginavalue = window.RCSAD_type_page;
                    }
                } else {
                    window.tipopaginavalue = 'nondefinito';
                }
            }

            // REMOVE FROM GPT -> usare rcsadVars.sitoDeviceTipoValue
            // KVALUE sito_device_tipo START
            /*
            const SITE_MAPPING = {
                'corriere.it/viaggi': 'Dove',
                'corriere.it': 'Corriere',
                'gazzetta.it': 'Gazzetta',
                'La7.it/la7d': 'LA7d',
                'La7.it': 'LA7',
                'tgla7.it': 'TGLA7',
                'sedanoallegro.it': 'Sedanoallegro',
                'abitare.it': 'Abitare',
                'amica.it': 'Amica',
                'iodonna.it': 'Iodonna',
                'oggi.it': 'Oggi',
                'style.it': 'Style',
                'living.it': 'Living',
                'lasicilia.it': 'Lasicilia',
                'unionesarda.it': 'Unionesarda',
                'leitv.it': 'Leitv',
                'open.online': 'Open',
                'gazzanet.gazzetta': 'Gazzanet',
                sosfanta: 'Sosfanta',
                violanews: 'Violanews',
                cittaceleste: 'Cittaceleste',
                ilmilanista: 'Ilmilanista',
                toronews: 'Toronews',
                forzaroma: 'Forzaroma',
                hellas: 'Hellas1903',
                pianetamilan: 'Pianetamilan',
                mediagol: 'Mediagol',
                tuttobolognaweb: 'Tuttobolognaweb',
                mondoudinese: 'Mondoudinese',
                padovasport: 'Padovasport',
                fcinter: 'Fcinter1908',
                fantamagazine: 'Fantamagazine',
                itasportpress: 'Itasportpress',
                golssip: 'Golssip',
                numericalcio: 'Numericalcio',
                calcionapoli1926: 'Calcionapoli1926',
                derbyderbyderby: 'Derbyderbyderby',
                juvenews: 'Juvenews',
                milanistichannel: 'Milanistichannel',
            };
            */

            // DEFINISCO EVENTI CUSTOM

            // EVENT CUSTOM 3 COLONNA CORRIERE QUANDO FRAME1 RENDERED
            const eventclmnsxresizeDFP = new Event('eventclmnsxresizeDFP');

            // Eventi load-ghost
            const arrEventNameAdvPos = [
                { eventId: 'P1', slotId: 'rcsad_Position1', hideOnGhost: true },
                { eventId: 'P3_1', slotId: 'rcsad_Position3.1', hideOnGhost: true },
                { eventId: 'P3_2', slotId: 'rcsad_Position3.2', hideOnGhost: true },
                { eventId: 'P5', slotId: 'rcsad_Position5', hideOnGhost: true },
                { eventId: 'P7', slotId: 'rcsad_Position7', hideOnGhost: true },
                { eventId: 'Tl', slotId: 'rcsad_TopLeft', hideOnGhost: true },
                { eventId: 'Top', slotId: 'rcsad_Top', hideOnGhost: true },
                { eventId: 'Fl', slotId: 'rcsad_Floor', hideOnGhost: true },
                { eventId: 'F1', slotId: 'rcsad_Frame1', hideOnGhost: true },
                { eventId: 'F2', slotId: 'rcsad_Frame2', hideOnGhost: true },
                { eventId: 'F3', slotId: 'rcsad_Frame3', hideOnGhost: true },
                { eventId: 'Bt', slotId: 'rcsad_Bottom', hideOnGhost: true },
                { eventId: 'B1', slotId: 'rcsad_Bottom1', hideOnGhost: true },
                { eventId: 'B2', slotId: 'rcsad_Bottom2', hideOnGhost: true },
                { eventId: 'B3', slotId: 'rcsad_Bottom3', hideOnGhost: true },
                { eventId: 'B4', slotId: 'rcsad_Bottom4', hideOnGhost: true },
                { eventId: 'B5', slotId: 'rcsad_Bottom5', hideOnGhost: true },
                { eventId: 'B6', slotId: 'rcsad_Bottom6', hideOnGhost: true },
                { eventId: 'Bl', slotId: 'rcsad_BottomLeft', hideOnGhost: true },
                { eventId: 'Br', slotId: 'rcsad_BottomRight', hideOnGhost: true },
                { eventId: 'Md', slotId: 'rcsad_Middle', hideOnGhost: true },
                { eventId: 'Md1', slotId: 'rcsad_Middle1', hideOnGhost: true },
                { eventId: 'Sr', slotId: 'rcsad_SmallRight', hideOnGhost: true },
                { eventId: 'L1', slotId: 'rcsad_Left1', hideOnGhost: true },
                { eventId: 'R1', slotId: 'rcsad_Right1', hideOnGhost: true },
            ];

            arrEventNameAdvPos.forEach((advPos) => {
                const loadedEventName = `eventloaded${advPos.eventId}DFP`;
                const ghostEventName = `eventghost${advPos.eventId}DFP`;

                advPos.loaded = new Event(loadedEventName);
                advPos.ghost = new Event(ghostEventName);
                window[loadedEventName] = advPos.loaded;
                window[ghostEventName] = advPos.ghost;
            });

            // Position1
            if (gptConfig.context.adSizes.Position1) {
                // Position1 è anche display
                if (gptConfig.mobileIntropageDisplay === true) {
                    gptConfig.context.adSizes.Position1.push('mediumRectangle', 'halfPage', 'mobileInterstitial');
                }
            }

            // Frame1
            if (gptConfig.context.adSizes.Frame1) {
                // Handle Outstream exceptions
                if (gptConfig.prebid.outstream === true) {
                    gptConfig.context.adSizes.Frame1.push('mobileVideo');
                }
            }

            // Add fluid size if configured
            if (gptConfig.fluidSize === true) {
                gptConfig.context.adSizes.Frame1?.push('fluid');
                gptConfig.context.adSizes.Frame2?.push('fluid');
                gptConfig.context.adSizes.Frame3?.push('fluid');
                gptConfig.context.adSizes.Middle?.push('fluid');
                gptConfig.context.adSizes.Middle1?.push('fluid');
                gptConfig.context.adSizes.Middle2?.push('fluid');
                gptConfig.context.adSizes.Floor?.push('fluid');
                gptConfig.context.adSizes.TopLeft?.push('fluid');
            }

            // Position7
            if (gptConfig.context.adSizes.Position7) {
                if (gptConfig.position7 === true || getQueryParam('test-position7') === 'true' || getQueryParam('test-position7') === 'prod') {
                    gptConfig.context.adSizes.Position?.push(
                        'leaderboard',
                        'leaderboardProg',
                        'largeLeaderboard',
                        'customLeaderboard1',
                        'customLeaderboard2',
                        'customBanner1'
                    );
                }
            }

            if (gptConfig.context.adSizes.TopLeft) {
                // Handle mobile giant masthead
                if (gptConfig.mobileGiantMasthead === true) {
                    gptConfig.context.adSizes.TopLeft.push('masthead', 'mastheadProg', 'giantMasthead', 'giantMastheadProg');
                }
            }

            // REMOVE FROM GPT
            // Default Ad Size Configurations
            let DEFAULT_AD_SIZES;
            let applyAdSizeExceptions;

            if (gptConfig.isPartner) {
                // Partner ad sizes
                DEFAULT_AD_SIZES = {
                    F1: [
                        [300, 250],
                        [300, 251],
                    ],
                    FLR: [
                        [320, 50],
                        [320, 51],
                        [320, 100],
                        [320, 101],
                    ],
                    TOP: [[1920, 1080]],
                    TL: [
                        [728, 90],
                        [728, 91],
                        [940, 60],
                        [940, 61],
                        [940, 90],
                        [940, 91],
                        [970, 250],
                        [970, 251],
                        [940, 530],
                        [940, 531],
                        [1920, 1080],
                    ],
                    TL_MOBILE: [
                        [320, 50],
                        [320, 51],
                        [320, 100],
                        [320, 101],
                    ],
                    P1: [[1, 1]],
                    P3: [],
                    F2: [
                        [300, 250],
                        [300, 252],
                    ],
                    F3: [
                        [300, 250],
                        [300, 253],
                    ],
                    BL: [
                        [728, 90],
                        [728, 92],
                        [940, 60],
                        [940, 62],
                        [940, 90],
                        [940, 92],
                        [970, 250],
                        [970, 252],
                    ],
                    BR: [
                        [300, 250],
                        [300, 253],
                    ],
                    MD: [[620, 90]],
                    MD1: [
                        [300, 250],
                        [300, 254],
                    ],
                    MD2: [[320, 100]],
                    BT: 'fluid',
                    B1: [[300, 1]],
                    B2: 'fluid',
                    B3: 'fluid',
                    L1: [
                        [184, 90],
                        [184, 91],
                    ],
                    R1: [
                        [184, 90],
                        [184, 92],
                    ],
                    SR: [[300, 100]],
                };

                applyAdSizeExceptions = function (sizes, position) {
                    let modifiedSizes = [...sizes];

                    // Add fluid size if enabled globally
                    const fluidPositions = ['F1', 'FLR', 'F2', 'F3', 'BR', 'MD', 'MD1', 'MD2'];

                    if (gptConfig.fluidSize === true && fluidPositions.includes(position)) {
                        modifiedSizes.push('fluid');
                    }

                    switch (position) {
                        case 'F1':
                            if (RCSAD_sitepage.includes('La7.it')) {
                                const isSpecialSection =
                                    window.sc_sezione === 'podcast' ||
                                    window.sc_sezione === 'sherlock' ||
                                    window.sc_sezione === 'programmi' ||
                                    window.sc_sezione.includes('i-protagonisti') ||
                                    window.location.href === 'https://www.la7.it/i-responsabili' ||
                                    window.sc_sezione.includes('i-sondaggi') ||
                                    window.sc_sezione === 'la7prime' ||
                                    window.sc_sezione === 'mini-storia';

                                const isSpecialPage =
                                    window.LA7_pagetype === 'homepage' ||
                                    window.LA7_pagetype === 'video' ||
                                    (window.LA7_pagetype === 'altro' && !isMobile) ||
                                    (RCSAD_sitepage === 'La7.it/la7d/programmi' && !isMobile) ||
                                    window.sc_sezione1 === 'donpasta';

                                if (!(isSpecialSection || isSpecialPage)) {
                                    modifiedSizes.push([300, 600]);
                                }

                                if (
                                    !isMobile &&
                                    ((window.sc_sezione === 'eden' && window.location.pathname === '/eden') ||
                                        window.sc_sezione === 'dirette-tv' ||
                                        window.sc_sezione === 'figworldcup' ||
                                        window.sc_sezione === 'voicetown')
                                ) {
                                    modifiedSizes = [
                                        [300, 250],
                                        [300, 251],
                                    ];
                                }

                                if (isMobile && window.LA7_pagetype === 'homepage') {
                                    modifiedSizes.push([300, 600]);
                                }
                            } else if (RCSAD_sitepage.includes('open.online') && isMobile) {
                                modifiedSizes = [
                                    [300, 250],
                                    [300, 251],
                                    [300, 600],
                                ];
                            }

                            break;

                        case 'TL':
                            if (RCSAD_sitepage.includes('La7.it')) {
                                modifiedSizes = isMobile
                                    ? [
                                          [320, 50],
                                          [320, 51],
                                      ]
                                    : [
                                          [728, 90],
                                          [728, 91],
                                          [940, 60],
                                          [940, 61],
                                          [940, 90],
                                          [940, 91],
                                          [970, 250],
                                          [970, 251],
                                      ];
                            } else if (RCSAD_sitepage.includes('open.online')) {
                                modifiedSizes = isMobile
                                    ? [
                                          [320, 50],
                                          [320, 51],
                                          [320, 100],
                                          [320, 101],
                                      ]
                                    : [
                                          [728, 90],
                                          [728, 91],
                                          [940, 60],
                                          [940, 61],
                                          [940, 90],
                                          [940, 91],
                                          [970, 250],
                                          [970, 251],
                                          [1920, 1080],
                                      ];
                            } else if (RCSAD_sitepage.includes('leitv.it') && !isTablet) {
                                modifiedSizes = [
                                    [728, 90],
                                    [728, 91],
                                    [940, 60],
                                    [940, 61],
                                    [940, 90],
                                    [940, 91],
                                    [970, 250],
                                    [970, 251],
                                    [940, 530],
                                    [940, 531],
                                ];
                            } else if (RCSAD_sitepage.includes('leitv.it') && isTablet) {
                                modifiedSizes = [
                                    [728, 90],
                                    [728, 91],
                                    [940, 60],
                                    [940, 61],
                                    [940, 90],
                                    [940, 91],
                                    [940, 530],
                                    [940, 531],
                                ];
                            } else if (RCSAD_sitepage.includes('gazzetta.it/festival-trento')) {
                                modifiedSizes = isMobile
                                    ? [
                                          [320, 50],
                                          [320, 51],
                                      ]
                                    : [
                                          [728, 90],
                                          [728, 91],
                                      ];
                            } else if (RCSAD_sitepage.includes('gazzetta.it/minisito-dacia') && !isMobile) {
                                modifiedSizes = [[1920, 1080]];
                            }

                            // Handle TL skin exceptions
                            if (!isMobile && gptConfig.TLSizeRefrOrient !== true && gptConfig.skinontopleft === true) {
                                if (window.matchMedia('(orientation: portrait)').matches && isTablet) {
                                    modifiedSizes = [];
                                } else if (window.matchMedia('(orientation: landscape)').matches || !isTablet) {
                                    modifiedSizes = [
                                        [728, 90],
                                        [728, 91],
                                        [940, 60],
                                        [940, 61],
                                        [940, 90],
                                        [940, 91],
                                        [970, 250],
                                        [970, 251],
                                        [940, 530],
                                        [940, 531],
                                        [1920, 1080],
                                    ];
                                }
                            }

                            break;

                        case 'FLR':
                            if (RCSAD_sitepage.includes('open.online')) {
                                modifiedSizes = [
                                    [320, 50],
                                    [320, 51],
                                    [300, 50],
                                    [300, 51],
                                    [320, 100],
                                    [320, 101],
                                    [300, 100],
                                    [300, 101],
                                ];
                                const isOpenHomepage = window.RCSAD_tipo_pagina === 'homepage' && window.sc_sezione1 === '';

                                if (isMobile && isOpenHomepage) {
                                    modifiedSizes.splice(2, 1); // Remove [300, 50]
                                }
                            }

                            break;

                        case 'B1':
                            if (RCSAD_sitepage.includes('open.online')) {
                                modifiedSizes.push('fluid');
                            }

                            break;

                        case 'B2':
                            if (RCSAD_sitepage.includes('unionesarda.it')) {
                                modifiedSizes = ['fluid', [300, 250]];
                            }

                            break;

                        case 'SR':
                            if (RCSAD_sitepage.includes('open.online')) {
                                modifiedSizes = [
                                    [300, 100],
                                    [300, 102],
                                    [320, 100],
                                    [320, 102],
                                ];
                            }

                            break;

                        case 'P1':
                            if (gptConfig.mobileIntropageDisplay === true && isMobile) {
                                modifiedSizes.push([300, 250], [300, 600]);
                            }

                            break;

                        case 'P3':
                            if (window.screen.availWidth >= 1400) {
                                modifiedSizes = [
                                    [120, 600],
                                    [160, 600],
                                ];
                            }

                            break;

                        default:
                            break;
                    }

                    return modifiedSizes;
                };

                sizeCurPosDfpRcsTl = applyAdSizeExceptions(isMobile ? DEFAULT_AD_SIZES.TL_MOBILE : DEFAULT_AD_SIZES.TL, 'TL');
                sizeCurPosDfpRcsF1 = applyAdSizeExceptions(DEFAULT_AD_SIZES.F1, 'F1');
                sizeCurPosDfpRcsF2 = applyAdSizeExceptions(DEFAULT_AD_SIZES.F2, 'F2');
                sizeCurPosDfpRcsF3 = applyAdSizeExceptions(DEFAULT_AD_SIZES.F3, 'F3');
                sizeCurPosDfpRcsBr = applyAdSizeExceptions(DEFAULT_AD_SIZES.BR, 'BR');
                sizeCurPosDfpRcsBl = applyAdSizeExceptions(DEFAULT_AD_SIZES.BL, 'BL');
                sizeCurPosDfpRcsTop = applyAdSizeExceptions(DEFAULT_AD_SIZES.TOP, 'TOP');
                sizeCurPosDfpRcsMd = applyAdSizeExceptions(DEFAULT_AD_SIZES.MD, 'MD');
                sizeCurPosDfpRcsMd1 = applyAdSizeExceptions(DEFAULT_AD_SIZES.MD1, 'MD1');
                sizeCurPosDfpRcsMd2 = applyAdSizeExceptions(DEFAULT_AD_SIZES.MD2, 'MD2');
                sizeCurPosDfpRcsBt = applyAdSizeExceptions(DEFAULT_AD_SIZES.BT, 'BT');
                sizeCurPosDfpRcsB1 = applyAdSizeExceptions(DEFAULT_AD_SIZES.B1, 'B1');
                sizeCurPosDfpRcsB2 = applyAdSizeExceptions(DEFAULT_AD_SIZES.B2, 'B2');
                sizeCurPosDfpRcsB3 = applyAdSizeExceptions(DEFAULT_AD_SIZES.B3, 'B3');
                sizeCurPosDfpRcsSr = applyAdSizeExceptions(DEFAULT_AD_SIZES.SR, 'SR');
                sizeCurPosDfpRcsL1 = applyAdSizeExceptions(DEFAULT_AD_SIZES.L1, 'L1');
                sizeCurPosDfpRcsR1 = applyAdSizeExceptions(DEFAULT_AD_SIZES.R1, 'R1');
                sizeCurPosDfpRcsP1 = applyAdSizeExceptions(DEFAULT_AD_SIZES.P1, 'P1');
                sizeCurPosDfpRcsP3 = applyAdSizeExceptions(DEFAULT_AD_SIZES.P3, 'P3');
                sizeCurPosDfpRcsFlr = applyAdSizeExceptions(DEFAULT_AD_SIZES.FLR, 'FLR');
            } else {
                applyAdSizeExceptions = function (sizes, position) {
                    let modifiedSizes = [...(sizes || '')];

                    // Apply global conditions first, then handle position-specific logic
                    switch (position) {
                        case 'P3': {
                            // Position3
                            modifiedSizes = [];

                            // Add skyscraper sizes for large screens or special game view
                            const isQuestoQuelloGameView = window.location.href.includes('questoquello.gazzetta.it/game');

                            if (window.screen.availWidth >= 1400 || (isQuestoQuelloGameView && isDesktop)) {
                                modifiedSizes.push(SIZE_INVENTORY.skyscraper, SIZE_INVENTORY.wide_skyscraper);
                            }

                            break;
                        }

                        case 'F1': {
                            // Handle Gazzanet exceptions
                            if (RCSAD_sitepage.includes('gazzanet.it')) {
                                modifiedSizes.push(SIZE_INVENTORY.large_rectangle, SIZE_INVENTORY.mobile_interstitial);
                            }

                            // Handle sport/MTB exceptions
                            if (RCSAD_sitepage.indexOf('/sport-praticati/mtb') > -1 || RCSAD_sitepage.indexOf('templatecustom.it') > -1) {
                                if (typeof window.sezione !== 'undefined' && window.sezione !== 'mag' && window.sezione !== 'video') {
                                    modifiedSizes = sizeNormalizer(modifiedSizes);
                                }
                            }

                            break;
                        }

                        case 'F2': // Frame2 position
                        case 'F3': // Frame3 position
                            // Handle Gazzanet exceptions
                            if (RCSAD_sitepage.includes('gazzanet.it')) {
                                modifiedSizes.push(SIZE_INVENTORY.large_rectangle);
                            }

                            // Handle sport/MTB exceptions
                            if (RCSAD_sitepage.indexOf('/sport-praticati/mtb') > -1 || RCSAD_sitepage.indexOf('templatecustom.it') > -1) {
                                if (typeof window.sezione !== 'undefined' && window.sezione !== 'mag' && window.sezione !== 'video') {
                                    modifiedSizes = sizeNormalizer(modifiedSizes);
                                }
                            }

                            break;

                        case 'BR': // BR position
                            // Handle Gazzanet exceptions
                            if (RCSAD_sitepage.includes('gazzanet.it')) {
                                modifiedSizes.push(SIZE_INVENTORY.large_rectangle, SIZE_INVENTORY.mobile_interstitial);
                            }

                            break;

                        case 'TL': {
                            // TopLeft position
                            // Handle amica.it cases
                            if (
                                !isMobile &&
                                RCSAD_sitepage.includes('amica.it') &&
                                window.tipologia !== 'home' &&
                                window.tipologia !== 'categoria' &&
                                !(
                                    window.tipologia === 'pagina' &&
                                    typeof window.sc_sezione !== 'undefined' &&
                                    typeof window.sc_sezione1 !== 'undefined' &&
                                    ((window.sc_sezione === 'dv1' && window.sc_sezione1 === 'video') ||
                                        (window.sc_sezione === 'video' && window.sc_sezione1 === ''))
                                )
                            ) {
                                const pathnameBlacklist = [
                                    '/sfilata/*',
                                    '/sfilate/',
                                    '/tag/royal-family/',
                                    '/tag/dieta/',
                                    '/tag/stasera-in-tv/',
                                    '/oroscopo/oggi/',
                                    '/oroscopo/domani/',
                                    '/oroscopo/mese/',
                                    '/oroscopo/anno/*',
                                ];
                                const pathnameWhitelist = [];
                                let blacklistMatch = false;
                                let whitelistMatch = false;

                                pathnameBlacklist.every((entry) => {
                                    blacklistMatch = matchRule(window.location.pathname, entry);

                                    return !blacklistMatch;
                                });
                                pathnameWhitelist.every((entry) => {
                                    whitelistMatch = matchRule(window.location.pathname, entry);

                                    return !whitelistMatch;
                                });

                                if (!blacklistMatch && (pathnameWhitelist.length === 0 || whitelistMatch)) {
                                    modifiedSizes.push(SIZE_INVENTORY.skin);
                                }
                            }

                            // Special case for gazzetta.it/minisito-dacia
                            if (RCSAD_sitepage.includes('gazzetta.it/minisito-dacia') && !isMobile) {
                                modifiedSizes = [];
                                modifiedSizes.push(SIZE_INVENTORY.skin);

                                return modifiedSizes;
                            }

                            // Special case for cam/ pages
                            if (RCSAD_sitepage.includes('cam/') && !isMobile) {
                                modifiedSizes = [];
                                modifiedSizes.push(SIZE_INVENTORY.billboard, SIZE_INVENTORY.billboard_prog);

                                return modifiedSizes;
                            }

                            // Special case for Gazzetta.tv video
                            if (
                                RCSAD_sitepage === 'gazzetta.it/tv' &&
                                window.tipologia === 'video' &&
                                isDesktop &&
                                gptConfig.TLSizeRefrOrient === true
                            ) {
                                return [];
                            }

                            // Special case for tablet portrait with skin
                            if (
                                isTablet &&
                                gptConfig.skinontopleft === true &&
                                gptConfig.TLSizeRefrOrient === false &&
                                window.matchMedia('(orientation: portrait)').matches
                            ) {
                                return [];
                            }

                            // Special case for landscape tablet/desktop with skin
                            if (
                                ((isTablet && window.matchMedia('(orientation: landscape)').matches) || (!isMobile && !isTablet)) &&
                                gptConfig.skinontopleft === true &&
                                gptConfig.TLSizeRefrOrient === false
                            ) {
                                const skinSizes = [...modifiedSizes, SIZE_INVENTORY.skin];
                                const sizeCurPosDfpRcsTlScroll = [...modifiedSizes];

                                return skinSizes;
                            }

                            // Handle Gazzanet mobile case
                            if (isMobile && RCSAD_sitepage.includes('gazzanet.it')) {
                                return [
                                    [320, 50],
                                    [320, 100],
                                ];
                            }

                            // Handle iodonna.it cases
                            if (isMobile && RCSAD_sitepage.includes('iodonna.it')) {
                                if (window.tipologia === 'gallery') {
                                    return [
                                        [320, 50],
                                        [320, 51],
                                        [320, 100],
                                        [320, 101],
                                    ];
                                }

                                if (window.tipologia === 'homepage' || window.tipologia === 'articolo') {
                                    return [
                                        [320, 50],
                                        [320, 51],
                                        [320, 100],
                                        [320, 101],
                                        [320, 150],
                                        [320, 151],
                                    ];
                                }
                            }

                            // Handle iodonna.it oroscopo case
                            if (
                                !isMobile &&
                                RCSAD_sitepage.includes('iodonna.it') &&
                                window.sezione === 'oroscopo' &&
                                window.tipologia === 'homepage'
                            ) {
                                return [
                                    [728, 90],
                                    [728, 91],
                                    [940, 60],
                                    [940, 61],
                                    [940, 90],
                                    [940, 91],
                                    [970, 250],
                                    [970, 251],
                                    [940, 530],
                                    [940, 531],
                                ];
                            }

                            // Handle MTB and templatecustom.it cases
                            if (RCSAD_sitepage.includes('/sport-praticati/mtb') || RCSAD_sitepage.includes('templatecustom.it')) {
                                if (window.sezione === 'mercatino' || window.sezione === 'forum') {
                                    modifiedSizes = modifiedSizes.filter((size) => size[1] % 10 !== 1 && size[0] !== 940);
                                }
                            }

                            // Handle Neveitalia tablet portrait case
                            if (
                                RCSAD_sitepage.includes('/sport-praticati/neveitalia') &&
                                isTablet &&
                                window.matchMedia('(orientation: portrait)').matches
                            ) {
                                return [];
                            }

                            // Handle Giro d'Italia case
                            if (RCSAD_sitepage.includes('gazzetta.it/giro-ditalia')) {
                                modifiedSizes = modifiedSizes.filter((size) => {
                                    if (sc_sezione === 'livehub') {
                                        return size[0] !== 970 && size[1] !== 530 && size[1] !== 531;
                                    }

                                    return size[1] !== 530 && size[1] !== 531;
                                });
                            }

                            // Handle style.it cases
                            if (RCSAD_sitepage.includes('style.it')) {
                                if (isMobile) {
                                    modifiedSizes.push([320, 150], [320, 151]);
                                } else if (window.tipologia === 'articolo' || window.tipologia === 'fotogallery') {
                                    modifiedSizes.push([1920, 1080]);
                                }
                            }

                            // Handle corriere.it/cucina case
                            if (
                                RCSAD_sitepage.includes('corriere.it/cucina') &&
                                ((isTablet && window.matchMedia('(orientation: landscape)').matches) || !isMobile)
                            ) {
                                const type = window.tipologia;

                                if (type !== 'articolo' && type !== 'card' && type !== 'gallery' && type !== 'recipe') {
                                    modifiedSizes = [
                                        [728, 90],
                                        [728, 91],
                                        [940, 60],
                                        [940, 61],
                                        [940, 90],
                                        [940, 91],
                                        [970, 250],
                                        [970, 251],
                                    ];
                                    modifiedSizes.push([1920, 161], [1920, 251]);
                                } else {
                                    modifiedSizes = [
                                        [728, 90],
                                        [728, 91],
                                        [940, 60],
                                        [940, 61],
                                        [940, 90],
                                        [940, 91],
                                        [970, 250],
                                        [970, 251],
                                        [940, 530],
                                        [940, 531],
                                    ];
                                }
                            }

                            // Handle torinofc case
                            if (RCSAD_sitepage.includes('torinofc') && !isMobile) {
                                modifiedSizes = [
                                    [728, 90],
                                    [728, 91],
                                    [940, 60],
                                    [940, 61],
                                    [940, 90],
                                    [940, 91],
                                    [970, 250],
                                    [970, 251],
                                ];
                            }

                            break;
                        }

                        default:
                            // No specific exceptions for this position
                            break;
                    }

                    modifiedSizes = modifiedSizes.map((size) => SIZE_INVENTORY[size]);

                    return modifiedSizes;
                };

                Object.keys(gptConfig.context.adSizes).forEach((key) => {
                    gptConfig.context.adSizes[key] = gptConfig.context.adSizes[key].map((size) => SIZE_INVENTORY[size]);
                });

                /*
                SizeCurPosDfpRcsTl = applyAdSizeExceptions(gptConfig.context.adSizes.TL, 'TL');
                sizeCurPosDfpRcsF1 = applyAdSizeExceptions(gptConfig.context.adSizes.F1, 'F1');
                sizeCurPosDfpRcsF2 = applyAdSizeExceptions(gptConfig.context.adSizes.F2, 'F2');
                sizeCurPosDfpRcsF3 = applyAdSizeExceptions(gptConfig.context.adSizes.F3, 'F3');
                sizeCurPosDfpRcsTop = applyAdSizeExceptions(gptConfig.context.adSizes.TOP, 'TOP');
                sizeCurPosDfpRcsMd = applyAdSizeExceptions(gptConfig.context.adSizes.MD, 'MD');
                sizeCurPosDfpRcsMd1 = applyAdSizeExceptions(gptConfig.context.adSizes.MD1, 'MD1');
                sizeCurPosDfpRcsMd2 = applyAdSizeExceptions(gptConfig.context.adSizes.MD2, 'MD2');
                sizeCurPosDfpRcsBt = applyAdSizeExceptions(gptConfig.context.adSizes.BT, 'BT');
                sizeCurPosDfpRcsB1 = applyAdSizeExceptions(gptConfig.context.adSizes.B1, 'B1');
                sizeCurPosDfpRcsB2 = applyAdSizeExceptions(gptConfig.context.adSizes.B2, 'B2');
                sizeCurPosDfpRcsB3 = applyAdSizeExceptions(gptConfig.context.adSizes.B3, 'B3');
                sizeCurPosDfpRcsSr = applyAdSizeExceptions(gptConfig.context.adSizes.SR, 'SR');
                sizeCurPosDfpRcsSr2 = applyAdSizeExceptions(gptConfig.context.adSizes.SR2, 'SR2');
                sizeCurPosDfpRcsL1 = applyAdSizeExceptions(gptConfig.context.adSizes.L1, 'L1');
                sizeCurPosDfpRcsR1 = applyAdSizeExceptions(gptConfig.context.adSizes.R1, 'R1');
                sizeCurPosDfpRcsP1 = applyAdSizeExceptions(gptConfig.context.adSizes.P1, 'P1');
                sizeCurPosDfpRcsP3 = applyAdSizeExceptions(gptConfig.context.adSizes.P3, 'P3');
                sizeCurPosDfpRcsP5 = applyAdSizeExceptions(gptConfig.context.adSizes.P5, 'P5');
                sizeCurPosDfpRcsP7 = applyAdSizeExceptions(gptConfig.context.adSizes.P7, 'P7');
                sizeCurPosDfpRcsExit = applyAdSizeExceptions(gptConfig.context.adSizes.EXIT, 'EXIT');
                */
            }

            window.rcsOrchestrator?.ptm?.pushTracking({
                label: 'sizes_done',
                funnel: 'adv',
            });

            // REMOVE FROM GPT
            // Prebid Server Fix Sizes TODO
            if (gptConfig.prebid.server === true) {
                // Add size 320x480 on position1 mobile
                if (Boolean(gptConfig.context.adSizes.Position1) && !gptConfig.context.adSizes.Position1.toString().includes('320,480') && isMobile) {
                    gptConfig.context.adSizes.Position1.push([320, 480]);
                }

                // Add size 1800x1000 when 1920x1080 is present (top)
                if (
                    Boolean(gptConfig.context.adSizes.Top) &&
                    gptConfig.context.adSizes.Top.toString().includes('1920,1080') &&
                    !gptConfig.context.adSizes.Top.toString().includes('1800,1000')
                ) {
                    gptConfig.context.adSizes.Top.push([1800, 1000]);
                }

                // Add size 1800x1000 when 1920x1080 is present (topleft)
                if (
                    Boolean(gptConfig.context.adSizes.TopLeft) &&
                    gptConfig.context.adSizes.TopLeft.toString().includes('1920,1080') &&
                    !gptConfig.context.adSizes.TopLeft.toString().includes('1800,1000') &&
                    !RCSAD_sitepage.includes('gazzanet.it/juvenews') &&
                    !RCSAD_sitepage.includes('oggi.it')
                ) {
                    gptConfig.context.adSizes.TopLeft.push([1800, 1000]);
                }
            }

            runGptExtensions(gptExtensions, 'beforeSlotsDefinition');

            let sizeCurPosDfpRcs = [];
            let sizeCurPosDfpRcsEmpty;
            let sizeCurPosDfpRcsTlSkin;

            // Define slots
            gptLogger.debug('Slots definition started', gptConfig.context.adSizes);
            for (const [key, value] of Object.entries(gptConfig.context.adSizes)) {
                const posName = key;

                sizeCurPosDfpRcs = value;
                window.gptadslots.push(
                    googletag
                        .defineSlot('/35821442/' + RCSAD_sitepage + '/' + posName, sizeCurPosDfpRcs, 'rcsad_' + posName.replace(/Mobile-/g, ''))
                        .addService(googletag.pubads())
                        .setTargeting('gestione_exitpage', 'false')
                );
                const slot = window.gptadslots.at(-1);

                gptLogger.debug('Slot defined:', {
                    adUnit: '/35821442/' + RCSAD_sitepage + '/' + posName,
                    sizes: sizeCurPosDfpRcs,
                    divId: 'rcsad_' + posName.replace(/Mobile-/g, ''),
                });

                if (
                    typeof window.sezione !== 'undefined' &&
                    window.sezione === 'video' &&
                    (RCSAD_sitepage.includes('corriere.it') || RCSAD_sitepage.includes('gazzetta.it'))
                ) {
                    slot.addService(googletag.companionAds());
                } else if ((posName === 'TopLeft' || posName === 'BottomLeft') && gptConfig.TLSizeRefrOrient === true) {
                    sizeCurPosDfpRcsEmpty = [];
                    sizeCurPosDfpRcsTlSkin = posName === 'TopLeft' ? sizeCurPosDfpRcsTl.slice(0) : sizeCurPosDfpRcsBl.slice(0);

                    const mappingSizeRsTl = googletag
                        .sizeMapping()
                        .addSize([1077, 1338], sizeCurPosDfpRcsEmpty) // IPAD 3 PORTRAIT
                        .addSize([1050, 200], sizeCurPosDfpRcsTlSkin)
                        .addSize([1024, 692], sizeCurPosDfpRcsTlSkin)
                        .addSize([1024, 1366], sizeCurPosDfpRcsEmpty) // IPAD PRO PORTRAIT
                        .addSize([1024, 1292], sizeCurPosDfpRcsEmpty) // IPAD PRO PORTRAIT
                        .addSize([1024, 1272], sizeCurPosDfpRcsEmpty) // IPAD 2 AIR PORTRAIT
                        .addSize([980, 1217], sizeCurPosDfpRcsEmpty) // IPAD 2 PORTRAIT
                        .addSize([768, 954], sizeCurPosDfpRcsEmpty) // IPAD 6 PORTRAIT
                        .addSize([768, 928], sizeCurPosDfpRcsEmpty) // IPAD 2 PORTRAIT TRAFFICO
                        .addSize([768, 528], sizeCurPosDfpRcsTlSkin)
                        .addSize([0, 0], sizeCurPosDfpRcs)
                        .build();

                    slot.defineSizeMapping(mappingSizeRsTl);
                    if (RCSAD_sitepage === 'corriere.it/homepage/hpcorriere') {
                        slot.setTargeting('homepage_scroll', 'hp1');
                    }
                } else if (RCSAD_sitepage === 'corriere.it/homepage/hpcorriere') {
                    slot.setTargeting('homepage_scroll', 'hp1');
                } else if (posName === 'Bottom1') {
                    slot.setTargeting('inread', 'true');
                } else if (posName === 'Bottom' || posName === 'Bottom2' || posName === 'Bottom3') {
                    slot.setTargeting('native', posName.toLowerCase());
                }

                // SET KVALUE STICKY POSITION START

                // TOPLEFT
                const videoPlayer = document.querySelector('.player'); // Check videoplayer cook
                const stickyTlkv = Boolean(
                    (RCSAD_sitepage.includes('corriere.it') && // CORRIERE
                        ((isMobile && typeof window.sezione !== 'undefined' && window.sezione !== 'video') || // CORRIERE TOPLEFT STICKY MOBILE
                            (!isMobile &&
                                typeof window.sezione !== 'undefined' &&
                                typeof window.tipologia !== 'undefined' && // CORRIERE TOPLEFT STICKY DESKTOP
                                ((window.sezione === 'cook' && window.tipologia === 'articolo') ||
                                    (window.tipologia === 'recipe' && !videoPlayer) ||
                                    window.tipologia === 'gallery' ||
                                    window.tipologia === 'card' ||
                                    window.sezione === 'economia')))) ||
                        (RCSAD_sitepage.includes('gazzetta.it') && // GAZZETTA
                            (isMobile || // GAZZETTA TOPLEFT STICKY MOBILE
                                (!isMobile && typeof window.sezione !== 'undefined' && window.sezione !== 'videogazzetta')))
                );

                // FRAME1 // FRAME2 // FRAME3
                const stickyColonnaDxkv = Boolean(
                    !isMobile &&
                        (document.querySelectorAll('.sticky-top-container').length > 0 ||
                            document.querySelectorAll('.stickyTop').length > 0 ||
                            document.querySelectorAll('#sticky-container-frame1-inner').length > 0)
                );

                if (
                    (posName === 'TopLeft' && stickyTlkv === true) ||
                    ((posName === 'Frame1' || posName === 'Frame2' || posName === 'Frame3') && stickyColonnaDxkv === true)
                ) {
                    slot.setTargeting('sticky', 'true');
                }

                // SET KVALUE STICKY POSITION END

                // SET KVALUE TOPLEFT ABTest START
                const stickyTlkv22 =
                    RCSAD_sitepage.includes('corriere.it') &&
                    isMobile &&
                    (document.querySelector('body.type-home--2022') || document.querySelector('body.type-home--22'));

                if (posName === 'TopLeft' && stickyTlkv22) {
                    slot.setTargeting('ABtest', 'popB');
                } else if (posName === 'TopLeft') {
                    slot.setTargeting('ABtest', 'popA');
                }
                // SET KVALUE TOPLEFT ABTest END

                window.gptadslotsSRA.push(slot); // BATCH POPOLO ARRAY TUTTE POS
                // gptPrintLog('DFP gptadslotsSRA: ' + posName);

                /* AMAZON SLOT START */
                if (gptConfig.prebid.amazon === true) {
                    // Clean amazon sizes
                    let sizeCurPosDfpRcsAmz = sizeNormalizer(sizeCurPosDfpRcs);

                    // Clean 320x480 from P1
                    if (posName === 'Position1') {
                        sizeCurPosDfpRcsAmz = sizeCurPosDfpRcsAmz.filter((size) => !(size[0] === 320 && size[1] === 480));
                    }

                    if (gptConfig.prebid.outstream === true && posName === 'Frame1') {
                        window.gptadslotsSRAamz.push({
                            slotID: 'rcsad_' + posName,
                            slotName: '/35821442/' + RCSAD_sitepage + '/' + posName,
                            mediaType: 'multi-format',
                            multiFormatProperties: {
                                display: {
                                    sizes: sizeCurPosDfpRcsAmz,
                                },
                                video: {
                                    sizes: [[320, 240]],
                                },
                            },
                        });
                    } else {
                        window.gptadslotsSRAamz.push({
                            slotID: 'rcsad_' + posName,
                            slotName: '/35821442/' + RCSAD_sitepage + '/' + posName,
                            sizes: sizeCurPosDfpRcsAmz,
                        });
                    }
                }
                /* AMAZON SLOT END */
            }

            window.rcsOrchestrator?.ptm?.pushTracking({
                label: 'slot_done',
                funnel: 'adv',
            });
            runGptExtensions(gptExtensions, 'afterSlotsDefinition');
            googletag
                .pubads()
                .getSlots()
                .forEach((slot) => {
                    const slotId = slot.getSlotElementId().replace('rcsad_', '');
                    const keys = slot.getTargetingKeys();
                    const targeting = keys.reduce((acc, key) => {
                        const values = slot.getTargeting(key);

                        acc[key] = values.length === 1 ? values[0] : values;

                        return acc;
                    }, {});

                    gptLogger.debug(`${slotId} targetings`, targeting);
                });

            // INIZIO fix soloinpagina player Daily Motion
            let currHead;
            let elemCompanionDiv;

            if (typeof window.soloinpagina_fuori === 'undefined') {
                currHead = document.getElementsByTagName('head')[0];
                elemCompanionDiv = document.createElement('div');
                document.addEventListener('RCSVideoManager:AD_INIT', initSoloinpagina);
                googletag.companionAds().addEventListener('slotRenderEnded', (event) => {
                    console.log('DFP Slot has22 been rendered SLOT: ', event);
                    if (event.slot.getSlotElementId() === elemCompanionDiv.id) {
                        const companionContainsAd = !event.isEmpty;

                        if (companionContainsAd && event.size[0] === 2 && event.size[1] === 2) {
                            window.dispatchEvent(new Event('start_soloinpagina_fuori'));
                            document.addEventListener('RCSVideoManager:AD_END', (_) => {
                                window.dispatchEvent(new Event('stop_soloinpagina_fuori'));
                            });
                        }
                    }
                });
            } else if (window.soloinpagina_fuori === 'start') {
                window.dispatchEvent(new Event('start_soloinpagina_fuori'));
                document.addEventListener('RCSVideoManager:AD_END', (_) => {
                    window.dispatchEvent(new Event('stop_soloinpagina_fuori'));
                });
            }

            function initSoloinpagina(obj) {
                if (typeof obj.detail.adunit.preRoll !== 'undefined' && typeof window.flagInitSIP === 'undefined') {
                    const proxyGPT = document.createElement('script');

                    proxyGPT.type = 'text/javascript';
                    proxyGPT.src = 'https://imasdk.googleapis.com/js/sdkloader/gpt_proxy.js';
                    currHead.appendChild(proxyGPT);
                    elemCompanionDiv.id = 'rcsad_companionDiv';
                    elemCompanionDiv.style.cssText = 'position:absolute;width:2px;height:2px;opacity:0;';
                    document.body.appendChild(elemCompanionDiv);
                    googletag.defineSlot(obj.detail.adunit.preRoll, [2, 2], elemCompanionDiv.id).addService(googletag.companionAds());
                    googletag.companionAds().setRefreshUnfilledSlots(true);
                    googletag.pubads().enableVideoAds();
                    googletag.enableServices();
                    window.flagInitSIP = 'done';
                }
            }
            // FINE fix soloinpagina player Daily Motion

            // #1 - test no sticky video player
            const isNoStickyActive = typeof gptConfig !== 'undefined' && gptConfig.noStickyCompanion && location.href.includes('corriere.it');
            let currHeadNoStickyRule;
            const currHeadNoSticky = currHead;
            const elemCompanionNoStickyDiv = document.createElement('div');

            if (isNoStickyActive) {
                currHeadNoStickyRule = document.getElementById('rcsad_NoStickyRule');
                const proxyGPTNoSticky = document.createElement('script');

                proxyGPTNoSticky.type = 'text/javascript';
                proxyGPTNoSticky.src = 'https://imasdk.googleapis.com/js/sdkloader/gpt_proxy.js';
                currHeadNoSticky.appendChild(proxyGPTNoSticky);
                elemCompanionNoStickyDiv.id = 'rcsad_companionNoStickyDiv';
                elemCompanionNoStickyDiv.style.cssText = 'position:absolute;width:3px;height:3px;opacity:0;';
                document.body.insertBefore(elemCompanionNoStickyDiv, document.body.firstChild);
            }
            // FINE #1 - test no sticky video player

            googletag.pubads().addEventListener('slotRenderEnded', (event) => {
                if (!window.initBatchAfterSRA && RCSAD_listpos.includes(event.slot.getSlotElementId().replace('rcsad_', ''))) {
                    // Wait for first SRA slotRenderEnded

                    window.initBatchAfterSRA = event.slot.getSlotElementId();
                    gptPrintLog('initBatchAfterSRA', window.initBatchAfterSRA + ' is first SRA slot');
                }

                // Dispatch loaded and ghost events
                const advPosObj = arrEventNameAdvPos.find((pos) => pos.slotId === event.slot.getSlotElementId());

                if (advPosObj) {
                    let isLoaded = event.slot.getResponseInformation() !== null;
                    const loadedEvent = advPosObj.loaded;
                    const ghostEvent = advPosObj.ghost;
                    const slotDiv = document.getElementById(event.slot.getSlotElementId());

                    // Force soloinpagina native events to always dispatch the loaded event
                    if (Boolean(slotDiv) && slotDiv.classList.contains('soloinpagina')) {
                        isLoaded = true;
                    }

                    // Add skinLegacy class when necessary
                    if (
                        (event.slot.getSlotElementId() === 'rcsad_TopLeft' || event.slot.getSlotElementId() === 'rcsad_Top') &&
                        event.size[0] === 1920 &&
                        event.size[1] === 1081
                    ) {
                        document.body.classList.add('skinLegacy');
                    }

                    // Custom behaviour for topleft
                    if (event.slot.getSlotElementId() === 'rcsad_TopLeft') {
                        if (isLoaded) {
                            loadedEvent.payload = event;
                        }

                        const topLeft = document.querySelector('#rcsad_TopLeft');
                        const tlWrapper = document.querySelector('#rcsad_TopLeft_wrapper');

                        if (event.size[1] !== 1080 && event.size[1] !== 1081 && event.size[1] !== 1000) {
                            if (topLeft) {
                                topLeft.classList.remove('skinVa');
                                topLeft.classList.remove('skinHp');
                                tlWrapper.classList.remove('skinHp');
                            }
                        }
                    }

                    // Hide div if requested
                    if (!isLoaded && advPosObj.hideOnGhost && Boolean(slotDiv)) {
                        slotDiv.style.display = 'none';
                    }

                    const eventToDispatch = isLoaded ? loadedEvent : ghostEvent;

                    document.dispatchEvent(eventToDispatch);
                    gptLogger.debug(
                        `${event.slot.getSlotElementId().replace('rcsad_', '')} ${eventToDispatch.type} fired ${
                            event.slot.firstRenderCompleted ? 'after refresh' : ''
                        }`
                    );
                    if (!event.slot.firstRenderCompleted) {
                        event.slot.firstRenderCompleted = true;
                    }
                }

                // #2 - test no sticky video player
                if (event.slot.getSlotElementId() === 'rcsad_companionNoStickyDiv') {
                    const companionContainsAd = !event.isEmpty;

                    if (companionContainsAd && event.size[0] === 3 && event.size[1] === 3) {
                        if (!currHeadNoStickyRule) {
                            currHeadNoStickyRule = document.createElement('style');
                            currHeadNoStickyRule.id = 'rcsad_NoStickyRule';
                            currHeadNoSticky.appendChild(currHeadNoStickyRule);
                            currHeadNoStickyRule.appendChild(document.createTextNode('.sticky-video { display:none !important; }'));
                        }
                    } else if (currHeadNoStickyRule) {
                        currHeadNoStickyRule.remove();
                    }
                }
                // FINE #2 - test no sticky video player

                // >> RENDERED TOPLEFT << //
                if (event.slot.getSlotElementId() === 'rcsad_TopLeft') {
                    window.rcsOrchestrator?.ptm?.pushTracking({
                        label: 'impression_topleft',
                        funnel: 'adv',
                    });

                    // #3 - test no sticky video player
                    if (isNoStickyActive) {
                        const slotNoSticky = googletag
                            .defineSlot('/35821442/' + RCSAD_sitepage + '/CompanionNoSticky', [3, 3], elemCompanionNoStickyDiv.id)
                            .addService(googletag.companionAds())
                            .addService(googletag.pubads());

                        googletag.companionAds().setRefreshUnfilledSlots(true);
                        googletag.pubads().enableVideoAds();
                        googletag.enableServices();
                        // Googletag.display(slotNoSticky);
                        googletag.pubads().refresh([slotNoSticky]);
                    }
                    // #3 - test no sticky video player
                }

                // >> RENDERED TOPLEFT FASCIONI HP CORRIERE RESTYLE 2019 << //
                if (event.slot.getSlotElementId().includes('_TopLeft_strip_')) {
                    const TlcontainsAd = !event.isEmpty;

                    if (TlcontainsAd === false || event.size[1] === 1) {
                        setTimeout(() => {
                            const slotCurrentGhost = document.getElementById(event.slot.getSlotElementId());

                            if (
                                slotCurrentGhost &&
                                slotCurrentGhost.clientHeight <= 40 &&
                                slotCurrentGhost.querySelector('div[id^="google_ads_iframe_"]') &&
                                slotCurrentGhost.querySelector('div[id^="google_ads_iframe_"]').clientHeight <= 40
                            ) {
                                slotCurrentGhost.parentNode.parentNode.style.display = 'none';
                                gptPrintLog('dispatchEvent', 'eventghostTlStripDFP STRIP GHOST: ' + slotCurrentGhost);
                            } else {
                                gptPrintLog('dispatchEvent', 'eventloadedTlStripDFP STRIP LOADED: ' + event.slot.getSlotElementId());
                            }
                        }, 500);
                    } else {
                        gptPrintLog('dispatchEvent', 'eventloadedTlStripDFP STRIP LOADED: ' + event.slot.getSlotElementId());
                    }
                }

                // >> RENDERED FRAME1 << //
                if (event.slot.getSlotElementId() === 'rcsad_Frame1') {
                    // FRAME 1 COLONNA DESTRA HP CORRIERE >> EVENTO CUSTOM PER FRAME1 noEMPTY(con TIMEOUT)
                    document.dispatchEvent(eventclmnsxresizeDFP);
                    gptPrintLog('dispatchEvent', 'eventclmnsxresizeDFP no timeout');
                    setTimeout(() => {
                        document.dispatchEvent(eventclmnsxresizeDFP);
                        gptPrintLog('dispatchEvent', 'eventclmnsxresizeDFP with timeout');
                    }, 4000);
                }
            });

            (() => {
                const handler = (event) => {
                    if (event.slot.getSlotElementId() === 'rcsad_TopLeft') {
                        window.rcsOrchestrator?.ptm?.pushTracking({
                            label: 'response_topleft',
                            funnel: 'adv',
                        });
                        googletag.pubads().removeEventListener('slotResponseReceived', handler);
                    }
                };

                googletag.pubads().addEventListener('slotResponseReceived', handler);
            })();

            googletag.pubads().addEventListener('impressionViewable', (event) => {
                if (event.slot.getSlotElementId() === 'rcsad_TopLeft') {
                    window.rcsOrchestrator?.ptm?.pushTracking({
                        label: 'viewability_topleft',
                        funnel: 'adv',
                    });
                }

                gptLogger.debug(`${event.slot.getSlotElementId()} impression registered`);
            });

            // CONFIGURAZIONE ADHOC PER VIDEO CORRIERE
            if (typeof window.sezione !== 'undefined' && window.sezione === 'video') {
                googletag.companionAds().setRefreshUnfilledSlots(true);
                googletag.pubads().enableVideoAds();
            }

            // CONFIGURAZIONE DEFAULT
            googletag.pubads().enableSingleRequest();
            googletag.pubads().disableInitialLoad(); // BATCH DISABILITO INITIAL LOAD
            googletag.pubads().enableAsyncRendering();

            runGptExtensions(gptExtensions, 'beforeGlobalTargetings');

            // CONFIGURAZIONE PPID DA COOKIE s_fid
            if (gptConfig.ppid === true && tmsTools.getCookie('s_fid') !== '') {
                const hashCookieSfid = function () {
                    if (cookieSfid !== null && cookieSfid !== '') {
                        cookieSfidHashed = CryptoJS.MD5(cookieSfid).toString();
                        window.RcsDataLayer.cookieSfidHashed = cookieSfidHashed;
                        document.dispatchEvent(eventCookieSfidHashed);
                    }
                };

                document.addEventListener('eventCookieSfidHashed', (e) => {
                    gptPrintLog('[s_fid] hash_md5: ' + cookieSfidHashed);
                    googletag.pubads().setPublisherProvidedId(cookieSfidHashed);
                });

                let cookieSfid = '';

                if (tmsTools.getCookie('s_fid')) {
                    cookieSfid = tmsTools.getCookie('s_fid').toString();
                }

                let cookieSfidHashed = '';
                const eventCookieSfidHashed = new Event('eventCookieSfidHashed');

                if (cookieSfid !== null && cookieSfid !== '') {
                    hashCookieSfid();
                } else {
                    document.addEventListener('eventDFPready', (_) => {
                        hashCookieSfid();
                    });
                }
            }

            // Enable/disable lazy load via URL query param
            const url = new URL(window.location.href);
            const param = url.searchParams.get('lazy-load');

            if (param) {
                gptConfig.lazyLoadDfp.activation = param === 'true';
            }

            // Enable lazy load by config
            if (gptConfig.lazyLoadDfp.activation === true) {
                googletag.pubads().enableLazyLoad({
                    fetchMarginPercent: gptConfig.lazyLoadDfp.fetchMarginPercentDfp,
                    renderMarginPercent: gptConfig.lazyLoadDfp.renderMarginPercentDfp,
                    mobileScaling: gptConfig.lazyLoadDfp.mobileScalingDfp,
                });
            }

            // CONFIGURAZIONE PPID DA permutive-id
            if (
                (typeof gptConfig !== 'undefined' && typeof gptConfig.ppid_permutiveid !== 'undefined' && gptConfig.ppid_permutiveid === true) ||
                (typeof RCSAD_sitepage !== 'undefined' && RCSAD_sitepage.indexOf('gazzetta.it/sport-praticati/trekking') > -1)
            ) {
                document.addEventListener('eventPermutiveIdHashed', (e) => {
                    googletag.pubads().setPublisherProvidedId(permutiveIdHashed);
                });
            }

            // PERMUTIVE
            if (gptConfig.permutive === true) {
                const pdfps = window.localStorage.getItem('_pdfps');

                googletag.pubads().setTargeting('permutive', pdfps ? JSON.parse(pdfps) : []);
            }

            // PERMUTIVE ID HASHATO
            const permutiveId = localStorage.getItem('permutive-id');
            let permutiveIdHashed = '';
            const eventPermutiveIdHashed = new Event('eventPermutiveIdHashed');

            function hashPermutiveId() {
                if (permutiveId !== null && permutiveId !== '') {
                    permutiveIdHashed = CryptoJS.MD5(permutiveId).toString();
                    document.dispatchEvent(eventPermutiveIdHashed);
                }
            }

            if (permutiveId !== null && permutiveId !== '') {
                hashPermutiveId();
            } else {
                document.addEventListener('eventDFPready', (_) => {
                    hashPermutiveId();
                });
            }

            // PARAMETRI CUSTOM RANDOM CREATIVE TARGETING
            window.soggettoKValue = Math.floor(Math.random() * 4) + 1;
            googletag.pubads().setTargeting('soggetto', window.soggettoKValue.toString());

            // PARAMETRI CUSTOM  SEZIONE
            if (
                window.location.pathname === '/test/indexTestDfpPoc-HPsez.shtml' ||
                window.location.pathname === '/test/indexTestDfpPoc-FOGLIA.shtml'
            ) {
                googletag.pubads().setTargeting('Sezione', 'listino auto');
            }

            // PARAMETRI CUSTOM  LUID LSEGS
            if (typeof window.RcsDataLayer.user_lynxID !== 'undefined') {
                const luid = window.RcsDataLayer.user_lynxID === '' ? 'NoId' : window.RcsDataLayer.user_lynxID;
                const lsegs = window.RcsDataLayer.user_lynxSegments === '' ? 'NoSegs' : window.RcsDataLayer.user_lynxSegments;

                googletag.pubads().setTargeting('luid', luid);
                googletag.pubads().setTargeting('lsegs', lsegs);
            }

            // PARAMETRO CUSTOM advDFPRefresh
            if (tmsTools.getLocalStorage('refreshAdvCookie') !== null) {
                let kValue = '';

                if (tmsTools.getLocalStorage('refreshAdvCookie').includes('refresh_ce')) {
                    kValue = document.hidden ? 'autoRefreshNonInView' : 'autoRefreshInView';
                } else if (tmsTools.getLocalStorage('refreshAdvCookie') === document.location.origin + document.location.pathname) {
                    kValue = document.hidden ? 'autoRefreshNonInView' : 'humanRefresh';
                }

                if (kValue !== '') {
                    googletag.pubads().setTargeting('advDFPRefresh', kValue);
                }
            }

            // PARAMETRI CUSTOM  ESCLUSIONE EXPANDING
            if (
                !isMobile &&
                window.location.hostname !== 'www.kelkoo.it' &&
                gptConfig.context.pageType !== 'photogallery' &&
                window.sezione !== 'dataroom' &&
                window.sottosezione !== 'dataroom' &&
                gptConfig.context.pageType !== 'ricettevideo'
            ) {
                googletag.pubads().setTargeting('expandingADV', 'true');
            } else {
                googletag.pubads().setTargeting('expandingADV', 'false');
            }

            // PARAMETRI CUSTOM CHROME SANDBOX LABEL KV
            let targetingKV = 'other';
            const flagIncognito = sessionStorage.getItem('FP_PRVT_USER') && sessionStorage.getItem('FP_PRVT_USER') === 'true' ? '_incognito' : '';

            if ('cookieDeprecationLabel' in navigator) {
                navigator.cookieDeprecationLabel.getValue().then((label) => {
                    googletag.pubads().setTargeting('Chrome', label);
                });
            } else if (navigator.userAgent.includes('Edg')) {
                targetingKV = 'edge';
            } else if (navigator.userAgent.includes('Chrome')) {
                targetingKV = 'control_1';
            } else if (navigator.userAgent.includes('Firefox')) {
                targetingKV = 'firefox';
            } else if (navigator.userAgent.includes('Safari')) {
                targetingKV = 'safari';
            } else if (navigator.userAgent.includes('Android')) {
                targetingKV = 'android';
            }

            googletag.pubads().setTargeting('Chrome', targetingKV + flagIncognito);

            // PARAMETRI CUSTOM BFCache
            if (isBFCacheReloaded) {
                googletag.pubads().setTargeting('BFCache', 'true');
                isBFCacheReloaded = false;
            }

            // PARAMETRI CUSTOM  tipo_pagina PAGINA
            googletag.pubads().setTargeting('tipo_pagina', gptConfig.context.pageType);

            // PARAMETRI CUSTOM  sito_device_tipo
            googletag.pubads().setTargeting('sito_device_tipo', `${gptConfig.context.site}_${getDevice()}_${gptConfig.context.pageType}`);

            // TARGETING COOKIE Omniture UESeg
            if (gptConfig.omnitourCookie === true && tmsTools.getCookie('UESeg') !== null) {
                googletag.pubads().setTargeting('RCSadobe', tmsTools.getCookie('UESeg'));
            }

            // TARGETING COOKIE Omniture marketingCloudVisitorID
            if (typeof omniture !== 'undefined' && typeof omniture.marketingCloudVisitorID !== 'undefined' && omniture.marketingCloudVisitorID) {
                googletag.pubads().setTargeting('RCSadobe', omniture.marketingCloudVisitorID);
            }

            // PARAMETRI CUSTOM  gestione_AMP
            googletag.pubads().setTargeting('gestione_AMP', 'false');

            // PARAMETRI CUSTOM  URL (KW1)
            function getParameterByNameKwUrl(name, url) {
                if (!url) url = window.location.href;
                name = name.replace(/[\[\]]/g, '\\$&');
                const regex = new RegExp('[?&]' + name + '(=([^&#]*)|&|#|$)');
                const results = regex.exec(url);

                if (!results) return null;
                if (!results[2]) return '';

                return decodeURIComponent(results[2].replace(/\+/g, ' '));
            }

            const keywordUrlValue = getParameterByNameKwUrl('keyword');
            const urlPageKvalue = window.location.href
                .toLowerCase()
                .replace(/[^a-z]/g, ' ')
                .replace(/\s\s+/g, ' ')
                .split(' ');

            if (keywordUrlValue) {
                urlPageKvalue.push(keywordUrlValue.replace(/,/g, ' ').split(' '));
                googletag.pubads().setTargeting('keyword', urlPageKvalue);
            }

            // PARAMETRI CUSTOM CONDIZIONI METEO
            function mappaOutlook(outlook) {
                const tabellaMappatura = [
                    'sereno',
                    'sole_e_caldo',
                    'poco_nuvoloso',
                    'nubi_sparse',
                    'pioggia_e_schiarite',
                    'pioggia_mista_a_neve_e_schiarite',
                    'neve_e_schiarite',
                    'coperto',
                    'pioggia_debole',
                    'pioggia',
                    'neve',
                    'pioggia_mista_a_neve',
                    'temporale',
                    'nebbia',
                    'nebbia_al_mattino',
                    'temporale_e_schiarite',
                    'grandine',
                    'neve_debole',
                    'temporale_con_grandine_e_schiarite',
                    'temporale_con_grandine',
                    'poco_nuvoloso_e_caldo',
                    'sole_e_caldo',
                    'nubi_sparse_e_caldo',
                    'caldo_estremo',
                ];

                return outlook && outlook > 0 ? tabellaMappatura[outlook - 1] : 'nessuno';
            }

            function mappaPrecipitationType(pt) {
                const tabellaMappatura = {
                    0: 'nessuna',
                    11: 'pioggia',
                    12: 'neve',
                    13: 'ghiaccio',
                    14: 'brina',
                };

                return pt && pt > 0 ? tabellaMappatura[pt] : 'nessuna';
            }

            function mappaAltitude(alt) {
                if (alt >= 0 && alt <= 600) return 'pianura';
                if (alt >= 601 && alt <= 999) return 'collina';
                if (alt >= 1000 && alt <= 1999) return 'bassa_montagna';
                if (alt >= 2000 && alt <= 2999) return 'media_montagna';
                if (alt >= 3000) return 'alta_montagna';

                return 'nessuna';
            }

            function mappaUv(uv) {
                return typeof uv === 'undefined' ? 'nessuno' : Math.min(Math.floor(uv), 4);
            }

            function setMeteoKv(city) {
                if (typeof city !== 'undefined') {
                    tmsTools.setCookie('meteoCity', city, 1);
                    fetch('https://api-weather-be.corriere.it/api/weather/' + city)
                        .then((response) => {
                            if (!response.ok) {
                                throw new Error(`Errore nella richiesta:${response.statusText}`);
                            }

                            return response.json();
                        })
                        .then((data) => {
                            const outlook = mappaOutlook(data.outlook);
                            const temp = Math.round(data.temperature);
                            const sea = data.sea ? 'true' : 'false';
                            const precipitationType = mappaPrecipitationType(data.precipitationType);
                            const altitude = mappaAltitude(data.altitude);
                            const uv = mappaUv(data.uv);

                            tmsTools.setCookie('meteoOutlook', outlook, 3 / 24);
                            tmsTools.setCookie('meteoTemp', temp.toString(), 3 / 24);
                            tmsTools.setCookie('meteoSea', sea, 3 / 24);
                            tmsTools.setCookie('meteoAltitude', altitude, 3 / 24);
                            tmsTools.setCookie('meteoUv', uv.toString(), 3 / 24);
                            tmsTools.setCookie('meteoPrecipitationType', precipitationType, 3 / 24);
                        })
                        .catch((error) => {
                            console.error('Errore:', error);
                        });
                }
            }

            if (typeof permutive !== 'undefined') {
                const resMeteoKvCond = tmsTools.getCookie('meteoOutlook') && tmsTools.getCookie('meteoTemp');
                const resMeteoKvCity = tmsTools.getCookie('meteoCity');

                if (resMeteoKvCond) {
                    const meteoCity = resMeteoKvCity ? resMeteoKvCity : tmsTools.getCookie('meteoCity') ? tmsTools.getCookie('meteoCity') : 'nessuna';
                    const meteoOutlook = tmsTools.getCookie('meteoOutlook');
                    const meteoTemperature = tmsTools.getCookie('meteoTemp');
                    const meteoSea = tmsTools.getCookie('meteoSea');
                    const meteoAltitude = tmsTools.getCookie('meteoAltitude');
                    const meteoUv = tmsTools.getCookie('meteoUv');
                    const meteoPrecipitationType = tmsTools.getCookie('meteoPrecipitationType');

                    googletag
                        .pubads()
                        .setTargeting('outlook', meteoOutlook)
                        .setTargeting('temperature', meteoTemperature)
                        .setTargeting('sea', meteoSea)
                        .setTargeting('altitude', meteoAltitude)
                        .setTargeting('uv', meteoUv)
                        .setTargeting('precipitationType', meteoPrecipitationType);
                    if (meteoCity !== 'nessuna') {
                        googletag.pubads().setTargeting('city', meteoCity);
                    }
                } else if (resMeteoKvCity) {
                    setMeteoKv(resMeteoKvCity);
                } else if (typeof permutive.context !== 'undefined' && typeof permutive.context.city !== 'undefined') {
                    setMeteoKv(permutive.context.city);
                } else {
                    permutive.once('Pageview', (_, __) => {
                        if (typeof permutive.context !== 'undefined' && typeof permutive.context.city !== 'undefined') {
                            setMeteoKv(permutive.context.city);
                        }
                    });
                }
            }

            // PARAMETRI CUSTOM  META SPECIALE ADV (KW2) //
            let kw2 = '';

            window.metaKeywords = function () {
                const metaCollection = document.getElementsByTagName('meta');

                for (let i = 0; i < metaCollection.length; i++) {
                    if (metaCollection[i].name === 'specialeadv') {
                        kw2 = metaCollection[i].content;
                    }
                }

                return kw2;
            };

            window.metaKeywords();
            if (kw2 !== '') {
                googletag.pubads().setTargeting('metatag', kw2);
            }

            // PARAMETRI CUSTOM  URL PER TEST LUNGHEZZA STRINGA CHIAMATA (KW3) //
            const stringUrlValue = getParameterByNameKwUrl('urldfp');

            if (stringUrlValue !== null && stringUrlValue === 'true') {
                googletag.pubads().setTargeting('urldfp', window.location.href);
            }

            // PARAMETRI CUSTOM PREMIUM GAZZETTA //
            if (RCSAD_sitepage.includes('gazzetta.it') && window.RCSAD_premium === true && window.RCSAD_abbonato === false) {
                gptPrintLog('contenuto_premium kvalue');
                googletag.pubads().setTargeting('contenuto_premium', 'true');
            }

            // PARAMETRI CUSTOM FIRME //
            if (typeof firmaTarg !== 'undefined') {
                googletag.pubads().setTargeting('gestione_firme', 'true');
                googletag.pubads().setTargeting('nome_giornalista', firmaTarg);
            }

            // PARAMETRI CUSTOM AB TEST //
            if (typeof advValueAbTest !== 'undefined') {
                if (advValueAbTest === true) {
                    googletag.pubads().setTargeting('abtest', 'popA');
                } else {
                    googletag.pubads().setTargeting('abtest', 'popB');
                }
            }

            // PARAMETRI CUSTOM  topleft+top //
            if (!isMobile && RCSAD_listpos.includes('Top,') && RCSAD_listpos.includes('TopLeft,')) {
                googletag.pubads().setTargeting('top&topleft', 'true');
            } else {
                googletag.pubads().setTargeting('top&topleft', 'false');
            }

            // MANINE CORRIERE
            if (tmsTools.getCookie('_pc_ADV_MANINA') !== null && typeof window.tipologia !== 'undefined' && window.tipologia === 'articolo') {
                if (tmsTools.getCookie('_pc_ADV_MANINA') === '1') {
                    googletag.pubads().setTargeting('manine', 'true');
                } else {
                    googletag.pubads().setTargeting('manine', 'false');
                }
            } else {
                googletag.pubads().setTargeting('manine', 'false');
            }

            // AB TEST BOX GRAFICA PIANO
            if (tmsTools.getCookie('_pc_box_grafica') !== null) {
                if (tmsTools.getCookie('_pc_box_grafica') === 'A') {
                    googletag.pubads().setTargeting('abtest', 'popA');
                } else {
                    googletag.pubads().setTargeting('abtest', 'popB');
                }
            }

            // KVALUE ABBONAMENTO ATTIVO CORRIERE
            if (RCSAD_sitepage.includes('corriere.it') && typeof utag !== 'undefined' && typeof window.RcsDataLayer !== 'undefined') {
                googletag
                    .pubads()
                    .setTargeting(
                        'exclude_nsb',
                        tmsTools.getCookie('rcsLogin') !== null &&
                            window.RcsDataLayer.trackAbbActive !== null &&
                            window.RcsDataLayer.trackAbbActive !== undefined
                            ? 'true'
                            : 'false'
                    );
            }

            // KVALUE INVENTORY TYPE
            googletag.pubads().setTargeting('inventory_type', isMobile ? 'mWeb' : 'web');

            // KVALUE POSITION1
            googletag.pubads().setTargeting('pos1', RCSAD_listpos.includes('Position1,').toString());

            // KVALUE gestione_APP
            let gestioneappValue;

            if (
                RCSAD_sitepage.includes('corriere.it') ||
                RCSAD_sitepage.includes('gazzetta.it') ||
                RCSAD_sitepage.includes('gazzanet.it') ||
                (gptConfig.isPartner && typeof window.RCSAD_app === 'undefined')
            ) {
                gestioneappValue = 'false';
            }

            if (document.location.search.includes('app_v2=true') || (gptConfig.isPartner && typeof window.RCSAD_app !== 'undefined')) {
                gestioneappValue = 'true';
            }

            if (typeof gestioneappValue !== 'undefined') {
                googletag.pubads().setTargeting('gestione_APP', gestioneappValue);
            }

            // KVALUE lowend-device
            const isLowEndDevice =
                (navigator.userAgent || navigator.vendor || window.opera).toLowerCase().includes('android') &&
                'deviceMemory' in navigator &&
                navigator.deviceMemory < 8;

            googletag.pubads().setTargeting('lowend-device', String(isLowEndDevice));

            googletag.enableServices();

            // SetTimeout(function () {

            // REFRESH PER POSIZIONI SOLO SE DIVERSO DA SEZIONE VIDEO, DOVE NON ESEGUO LA REFRESH
            if (
                typeof window.sezione === 'undefined' ||
                window.sezione !== 'video' ||
                (siteNameadv !== 'gazzetta.it' && siteNameadv !== 'corriere.it')
            ) {
                if (gptConfig.prebid.activation === false && typeof startPrebidRenderAd === 'undefined') {
                    googletag.pubads().refresh(window.gptadslotsSRA); // BATCH REFRESH POSIZIONI IN SRA TRANNE CHE SPETTACOLI (PREBID)
                }
            } else {
                gptPrintLog('no refresh chiamata DFP originale');
            }
            // }, 0);

            window.rcsOrchestrator?.ptm?.pushTracking({
                label: 'targeting_done',
                funnel: 'adv',
            });
            runGptExtensions(gptExtensions, 'afterGlobalTargetings');
            gptLogger.debug(
                'Page targetings',
                googletag
                    .pubads()
                    .getTargetingKeys()
                    .reduce((acc, key) => {
                        const values = googletag.pubads().getTargeting(key);

                        acc[key] = values.length === 1 ? values[0] : values;

                        return acc;
                    }, {})
            );

            /* Prebid Start */
            if (gptConfig.prebid.activation === true) {
                if (gptConfig.prebid.server === true) {
                    // Initialize pbjs
                    window.pbjs = window.pbjs || {};
                    window.pbjs.que = window.pbjs.que || [];

                    // Setup googletag
                    window.googletag = window.googletag || {};
                    window.googletag.cmd = window.googletag.cmd || [];

                    pbjs.que.push(() => {
                        pbjs.onEvent('bidWon', (data) => {
                            gptPrintLog('Prebid Server', `bidWon on ${data.adUnitCode} by ${data.bidder}`);
                        });
                    });

                    if (gptConfig.prebid.amazon === true) {
                        executeParallelAuction('Prebid Server SRA', window.gptadslotsSRA, window.gptadslotsSRAamz);
                    } else {
                        gptPrintLog('Prebid Server', 'auction Prebid only no APS');

                        googletag.pubads().disableInitialLoad();
                        googletag.pubads().enableSingleRequest();
                        googletag.enableServices();

                        // Function that calls the ad-server
                        const callAdserver = function (gptSlots, calledBy) {
                            if (pbjs.adserverCalled) return;

                            gptLogger.log('...auction ended');
                            window.rcsOrchestrator?.ptm?.pushTracking({
                                label: 'auction_done',
                                funnel: 'adv',
                            });
                            if (calledBy === 'failsafe') {
                                gptPrintLog('Prebid Server', 'requested bids pbjs failsafe');
                                pbjs.adserverCalled = 'failsafe';
                            } else {
                                gptPrintLog('Prebid Server', 'requested bids pbjs loaded');
                                pbjs.adserverCalled = 'loaded';
                            }

                            runGptExtensions(gptExtensions, 'beforeRefresh');
                            googletag.pubads().refresh(gptSlots);
                            runGptExtensions(gptExtensions, 'afterRefresh');
                        };

                        // Request pbjs bids when it loads
                        pbjs.que.push(() => {
                            gptLogger.log('Parallel auction started...');
                            window.rcsOrchestrator?.ptm?.pushTracking({
                                label: 'auction',
                                funnel: 'adv',
                            });
                            pbjs.rp.requestBids({
                                callback: callAdserver,
                                gptSlotObjects: window.gptadslotsSRA,
                            });
                        });

                        // Failsafe in case PBJS doesn't load
                        setTimeout(() => {
                            if (pbjs.adserverCalled === 'loaded') return;
                            callAdserver(window.gptadslotsSRA, 'failsafe');
                        }, gptConfig.FAILSAFE_TIMEOUT);
                    }
                }
            }
            /* Prebid End */
        });

        // FUNCTION DEFSLOT E REFRESH POISIZIONI IN BATCH //
        window.refreshBatchPageSlots = function (pos, adunit, div, framex, flagBrX) {
            if (framex) {
                pos = 'Framex';
            }

            // Check position existence in the batchAdSizes object and make sure to not remap in case che position is iterative like FrameX
            if (gptConfig.context.batchAdSizes[pos].length > 0 && typeof gptConfig.context.batchAdSizes[pos][0] === 'string') {
                gptConfig.context.batchAdSizes[pos] = gptConfig.context.batchAdSizes[pos].map((size) => SIZE_INVENTORY[size]);
            }

            const size = gptConfig.context.batchAdSizes[pos] || [];

            const posBatchPrebidIncluded =
                pos === 'BoxUp' ||
                pos === 'BoxDown' ||
                pos === 'Middle' ||
                pos === 'Middle2' /* || pos === "Bottom2" */ ||
                pos === 'Floor' ||
                framex !== undefined;
            const posBatchGeoeditriceBrX =
                pos === 'BottomRight' &&
                flagBrX !== undefined &&
                (RCSAD_sitepage.includes('cittaceleste') ||
                    RCSAD_sitepage.includes('juvenews') ||
                    RCSAD_sitepage.includes('ilmilanista') ||
                    RCSAD_sitepage.includes('mondoudinese') ||
                    RCSAD_sitepage.includes('notiziecalciomercato'));

            // PREBID SERVER BATCH
            let gptadslotsSRAamzBatch;

            if (gptConfig.prebid.amazon === true && posBatchPrebidIncluded) {
                gptadslotsSRAamzBatch = [
                    {
                        slotID: div,
                        slotName: '/35821442/' + RCSAD_sitepage + '/' + pos,
                        sizes: size,
                    },
                ];
            }

            let gptadslotBatchPage;

            // PASSO K-VALUE "native" SOLTANTO A POSIZIONI NATIVE
            if (['Bottom', 'Bottom2', 'Bottom3'].includes(pos)) {
                if (
                    !isMobile &&
                    (RCSAD_sitepage === 'corriere.it/homepage/hpcorriere' || RCSAD_sitepage === 'gazzetta.it/homepage') &&
                    gptConfig.lazyLoadDfp.activation === false
                ) {
                    gptadslotBatchPage = googletag.defineSlot('/35821442/' + RCSAD_sitepage + adunit, size, div);
                    if (gptadslotBatchPage) {
                        gptadslotBatchPage
                            .addService(googletag.pubads())
                            .setTargeting('gestione_exitpage', 'false')
                            .setTargeting('native', pos.toLowerCase())
                            .setTargeting('native-inview', 'false');
                    }
                } else {
                    let appPos = pos.toLowerCase();

                    if (isMobile && window.location.href.includes('living.corriere.it') && window.tipologia === 'card' && pos === 'Bottom3') {
                        appPos = 'bottom2';
                    }

                    gptadslotBatchPage = googletag.defineSlot('/35821442/' + RCSAD_sitepage + adunit, size, div);
                    if (gptadslotBatchPage) {
                        gptadslotBatchPage.addService(googletag.pubads()).setTargeting('gestione_exitpage', 'false').setTargeting('native', appPos);
                    }
                }
            } else if (div === 'rcsad_TopLeftExt' || div === 'rcsad_TopLeft_Piano') {
                // PASSO K-VALUE "gestione_exitpage" SOLTANTO A POSIZIONE EXIT-PAGE
                gptadslotBatchPage = googletag.defineSlot('/35821442/' + RCSAD_sitepage + adunit, size, div);
                if (gptadslotBatchPage) {
                    gptadslotBatchPage.addService(googletag.pubads()).setTargeting('gestione_exitpage', 'true');
                }
            } else if (pos.includes('BottomLeft') || pos.includes('TopLeft')) {
                const sizeCurPosDfpRcsEmptyTlBl = [];
                const mappingSizeRsTlBl = googletag
                    .sizeMapping()
                    .addSize([1077, 1338], sizeCurPosDfpRcsEmptyTlBl) // IPAD 3 PORTRAIT
                    .addSize([1050, 200], size)
                    .addSize([1024, 692], size)
                    .addSize([1024, 1366], sizeCurPosDfpRcsEmptyTlBl) // IPAD PRO PORTRAIT
                    .addSize([1024, 1292], sizeCurPosDfpRcsEmptyTlBl) // IPAD PRO PORTRAIT
                    .addSize([1024, 1272], sizeCurPosDfpRcsEmptyTlBl) // IPAD 2 AIR PORTRAIT
                    .addSize([980, 1217], sizeCurPosDfpRcsEmptyTlBl) // IPAD 2 PORTRAIT
                    .addSize([768, 954], sizeCurPosDfpRcsEmptyTlBl) // IPAD 6 PORTRAIT
                    .addSize([768, 928], sizeCurPosDfpRcsEmptyTlBl) // IPAD 2 PORTRAIT TRAFFICO
                    .addSize([768, 528], size)
                    .addSize([0, 0], size)
                    .build();

                gptadslotBatchPage = googletag.defineSlot('/35821442/' + RCSAD_sitepage + adunit, size, div);
                if (gptadslotBatchPage) {
                    gptadslotBatchPage.defineSizeMapping(mappingSizeRsTlBl).addService(googletag.pubads()).setTargeting('gestione_exitpage', 'false');
                }
            } else if (pos.includes('SmallRight')) {
                // PASSO K-VALUE "Small_Rectangle"
                gptadslotBatchPage = googletag.defineSlot('/35821442/' + RCSAD_sitepage + adunit, size, div);
                if (gptadslotBatchPage) {
                    gptadslotBatchPage
                        .addService(googletag.pubads())
                        .setTargeting('gestione_exitpage', 'false')
                        .setTargeting('Small_Rectangle', 'Small_Rectangle_Right');
                }
            } else if (posBatchGeoeditriceBrX) {
                if (flagBrX === 'first') {
                    // Rimozione size 320x480 solo per il primo BottomRightX
                    const sizeFirst = size.slice(0, 2);

                    gptadslotBatchPage = googletag.defineSlot('/35821442/' + RCSAD_sitepage + adunit, sizeFirst, div);
                    if (gptadslotBatchPage) {
                        gptadslotBatchPage
                            .addService(googletag.pubads())
                            .setTargeting('gestione_exitpage', 'false')
                            .setTargeting('BottomRight', 'first');
                    }
                } else if (flagBrX === 'last') {
                    gptadslotBatchPage = googletag.defineSlot('/35821442/' + RCSAD_sitepage + adunit, size, div);
                    if (gptadslotBatchPage) {
                        gptadslotBatchPage
                            .addService(googletag.pubads())
                            .setTargeting('gestione_exitpage', 'false')
                            .setTargeting('BottomRight', 'last');
                    }
                } else {
                    gptadslotBatchPage = googletag.defineSlot('/35821442/' + RCSAD_sitepage + adunit, size, div);
                    if (gptadslotBatchPage) {
                        gptadslotBatchPage.addService(googletag.pubads()).setTargeting('gestione_exitpage', 'false');
                    }
                }
            }
            // DI DEFAULT PASSO SOLTANTO K-VALUE gestione_exitpage=FALSE
            else {
                gptPrintLog('pos_check_bidd: ' + div + ', framex !== undefined: ' + (framex !== undefined));
                gptadslotBatchPage = googletag.defineSlot('/35821442/' + RCSAD_sitepage + adunit, size, div);
                if (gptadslotBatchPage) {
                    gptadslotBatchPage.addService(googletag.pubads()).setTargeting('gestione_exitpage', 'false');
                }
            }

            // Batch after SRA
            if (RCSAD_listpos !== '' && !window.initBatchAfterSRA) {
                googletag.pubads().addEventListener('slotRenderEnded', function batchAfterSRA(event) {
                    if (gptadslotBatchPage && event.slot && RCSAD_listpos.includes(event.slot.getSlotElementId().replace('rcsad_', ''))) {
                        // Wait for first SRA slotRenderEnded instead

                        window.initBatchAfterSRA = event.slot.getSlotElementId();
                        gptPrintLog('initBatchAfterSRA', gptadslotBatchPage.getSlotElementId());

                        if (gptadslotsSRAamzBatch) {
                            executeParallelAuction('Prebid Server Batch', [gptadslotBatchPage], gptadslotsSRAamzBatch);
                        } else {
                            googletag.pubads().refresh([gptadslotBatchPage]);
                        }

                        googletag.pubads().removeEventListener('slotRenderEnded', batchAfterSRA);
                    }
                });
            } else {
                if (gptadslotBatchPage) {
                    gptPrintLog('initBatchAfterSRA', gptadslotBatchPage.getSlotElementId());
                }

                if (gptadslotsSRAamzBatch) {
                    executeParallelAuction('Prebid Server Batch', [gptadslotBatchPage], gptadslotsSRAamzBatch);
                } else {
                    googletag.pubads().refresh([gptadslotBatchPage]);
                }
            }

            googletag.cmd.push(() => {
                googletag.display(div);
            });
        };

        // FUNCTION DEFSLOT E REFRESH POISIZIONI INFINITE SCROLL SRA //
        window.printScrollSlots = function (
            idScrollSlots,
            sitepageOverride = RCSAD_sitepage,
            targetingHpScroll = null,
            checkHalfPageExcluded = null
        ) {
            const batchSlotList = [];
            const amzBatchSlotList = [];

            function refreshBidFascioni() {
                pbjs.que.push(() => {
                    pbjs.requestBids({
                        timeout: gptConfig.PREBID_TIMEOUT,
                        bidsBackHandler() {
                            pbjs.setTargetingForGPTAsync();
                            googletag.pubads().refresh(batchSlotList, {
                                changeCorrelator: false,
                            });
                        },
                    });
                });
            }

            if (typeof idScrollSlots === 'string') {
                idScrollSlots = [...idScrollSlots];
            }
            googletag.cmd.push(() => {
                for (let i = 0; i < idScrollSlots.length; i++) {
                    let newSlot;
                    let amzSlotSizeArray;

                    let posName = idScrollSlots[i].replace('rcsad_', '');
                    let posIndex = posName;

                    if (posName.startsWith('TopLeft_strip')) {
                        posIndex = 'TopLeft_strip';
                        posName = 'TopLeft';
                    }

                    if (posName.startsWith('Frame1_strip')) {
                        posIndex = 'Frame1_strip';
                        posName = 'Frame1';
                    }

                    if (gptConfig.context.batchAdSizes[posIndex].length > 0) {
                        if (typeof gptConfig.context.batchAdSizes[posIndex][0] === 'string') {
                            gptConfig.context.batchAdSizes[posIndex] = gptConfig.context.batchAdSizes[posIndex].map((size) => SIZE_INVENTORY[size]);
                        }
                        newSlot = googletag.defineSlot(
                            `/35821442/${sitepageOverride}/${posName}`,
                            gptConfig.context.batchAdSizes[posIndex],
                            idScrollSlots[i]
                        );
                        if (newSlot) {
                            newSlot.addService(googletag.pubads()).setTargeting('gestione_exitpage', 'false');
                            if (posName === 'Frame1' && typeof targetingHpScroll !== 'undefined' && targetingHpScroll !== null) {
                                newSlot.setTargeting('homepage_scroll', targetingHpScroll);
                            }

                            if (posName === 'Bottom' || posName === 'Bottom2' || posName === 'Bottom3') {
                                newSlot.setTargeting('native', posName);

                                if (
                                    RCSAD_sitepage.toLowerCase().includes('living.it') ||
                                    RCSAD_sitepage.includes('style.it') ||
                                    RCSAD_sitepage.toLowerCase().includes('iodonna.it')
                                ) {
                                    newSlot.setTargeting('native', 'Bottom2');
                                }

                                if (!gptConfig.lazyLoadDfp.activation) {
                                    newSlot.setTargeting('native-inview', 'false');
                                }
                            }

                            if (posName === 'Bottom4') {
                                newSlot.setTargeting('native', 'Bottom2');
                                amzSlotSizeArray = gptConfig.context.batchAdSizes.Bottom2;
                            }

                            if (posName === 'Bottom5' || posName === 'Bottom6') {
                                newSlot.setTargeting('native', 'Bottom3');
                                amzSlotSizeArray = gptConfig.context.batchAdSizes.Bottom2;
                            }

                            if (posName.includes('Bacheca')) {
                                newSlot.setTargeting('bacheca', posName);
                            }

                            if (!amzSlotSizeArray) amzSlotSizeArray = gptConfig.context.batchAdSizes[posIndex];
                            batchSlotList.push(newSlot);
                            amzBatchSlotList.push({
                                slotID: newSlot.getSlotElementId(),
                                slotName: newSlot.getAdUnitPath(),
                                sizes: amzSlotSizeArray,
                            });
                            googletag.display(newSlot);
                        }
                    }
                }

                if (checkHalfPageExcluded === null) {
                    googletag.pubads().refresh(batchSlotList);
                } else if (gptConfig.prebid.activation === true) {
                    if (gptConfig.prebid.amazon === true) {
                        executeParallelAuction('Prebid Server Print Scroll', batchSlotList, amzBatchSlotList);
                    } else {
                        refreshBidFascioni();
                    }
                } else {
                    googletag.pubads().refresh(batchSlotList, {
                        changeCorrelator: false,
                    });
                }
            });
        };

        // GENERIC EVENT GOOGLE TAG READY
        gptPrintLog('dispatchEvent', 'eventDFPready');
        window.eventDFPready = document.createEvent('event');
        window.eventDFPready.initEvent('eventDFPready', true, true);
        document.dispatchEvent(window.eventDFPready);

        // FUNCTION REFRESH TABLET ORIENTATION CHANGE //
        if (isTablet && gptConfig.TLSizeRefrOrient === true) {
            window.skinResetVideoPage = function () {
                const skinCnt = document.getElementById('rcsad_Top') || document.getElementById('rcsad_TopLeft');

                if (skinCnt) {
                    skinCnt.className = skinCnt.className.replace(/\b(skin|skinVa)\b/g, '');
                }

                document.body.className = document.body.className.replace(/\b(skin|skinVa)\b/g, '');
                document.body.style.backgroundColor = 'transparent';

                if (document.getElementById('barra_click_sx')) {
                    document.getElementById('barra_click_sx').remove();
                }

                if (document.getElementById('barra_click_dx')) {
                    document.getElementById('barra_click_dx').remove();
                }

                if (document.getElementById('barra_click_top')) {
                    document.getElementById('barra_click_top').remove();
                }
            };

            window.addEventListener('orientationchange', () => {
                window.skinResetVideoPage();
                setTimeout(() => {
                    gptPrintLog('Orientamento cambiato. Faccio partire la refresh');
                    googletag.pubads().refresh();
                }, 500);
            });
        }

        runGptExtensions(gptExtensions, 'afterInitGptTag');
        window.rcsOrchestrator?.ptm?.pushTracking({
            label: 'init_gpt_tag_done',
            funnel: 'adv',
        });
    }
}
